import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Creatable from "react-select/creatable";
import DatePicker from "react-date-picker";
import { toast } from "react-toastify";
import { Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../layout/Layout";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { useMeCore } from "../../cores/me";
import { meApiService } from "../../cores/me/services/api";
import SyncAccount from "../component/SyncAccount/SyncAccount";
import { SocialProviderId, isInstagramSocialConnected } from "../../cores/socialPost/config";
import { SMART_TIMES } from "../../cores/socialPost/consts/smartTimes.constant";
import { isTiktokPostingEnabled } from "../../cores/config/featureFlags";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { getBase64 } from "../../cores/utils";
import { instagramApiService } from "../../cores/instagram/services/api";
import { instagramLoginApiService } from "../../cores/instagramLogin/services/api";
import { facebookApiService } from "../../cores/facebook/services/api";
import { linkedInApiService } from "../../cores/linkedIn/services/api";
import { twitterApiService } from "../../cores/twitter/services/api";
import { tiktokApiService } from "../../cores/tiktok/services/api";
import { Errors } from "../../constants/errors";
import { FacebookIcon } from "../component/social/Facebook/FacebookIcon";
import { InstagramIcon } from "../component/social/Instagram/InstagramIcon";
import { LinkedInIcon } from "../component/social/LinkedIn/LinkedInIcon";
import { TwitterIcon } from "../component/social/Twitter/TwitterIcon";
import { TiktokIcon } from "../component/social/Tiktok/TiktokIcon";
import {
  autoPostGenerationManager,
  AutoPostContentType,
  AutoPostGenTask,
  AutoPostJobState,
} from "../../cores/socialAutoPosts/generationManager";
import { socialAutoPostsApi } from "../../cores/socialAutoPosts/api";
import type {
  SocialPostImageAspect,
  SocialPostImageTextDensity,
} from "../../ui/component/socialPost/modals/buildSocialPostImagePrompt";
import { openAiSizeForAspect } from "../../ui/component/socialPost/modals/buildSocialPostImagePrompt";
import { StaticSocialPostPreview } from "../component/socialAutoPosts/StaticSocialPostPreview";
import { AiImageViewerEditorModal } from "../component/ai/AiImageViewerEditorModal";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";
import { WEMBII_AI_CREDITS_REFRESH_EVENT } from "../../constants/dom.constant";
import { useRouterCore } from "../../cores/router";
import { scheduleDateStringForApiFromDateAndHour } from "../../cores/socialPost/helpers/scheduleDateForApi";
import "../component/socialPost/preview/SocialPostPreview.scss";
import "../styles/SocialAutomatedPosts.scss";

type Option = { value: string; label: string };

type UploadedImage = {
  file: File;
  previewUrl: string;
  /** Durable http(s) URL when available (preferred for localStorage). */
  durableUrl?: string;
};

type StepId = "config" | "media" | "preview";

type ConfigMode = "guided" | "custom" | "auto";

// Proyecto = serie de publicaciones (se guarda para cerrar y retomar después).
type AutoPostProject = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  hasGeneratedContent?: boolean;
};

type PersistedGeneratedContent = {
  texts: Record<string, string>;
  images: Record<string, string>; // data URLs
  schedules?: Record<string, { dateIso: string; time: string }>;
  selectedSmartTime?: string | null;
  previewSeed?: number;
  scheduledPostIds?: string[];
};

// Borrador persistido por proyecto (config + paso + contenido generado).
type AutoPostDraft = {
  step: StepId;
  industry: string;
  topics: Option[];
  selectedDays: string[];
  startDateIso: string | null;
  additionalInfo?: string;
  contentType?: AutoPostContentType;
  durationWeeks?: number;
  configMode?: ConfigMode;
  customContentBrief?: string;
  customPostTopics?: string[];
  brandDescription?: string;
  includeLogo?: boolean;
  imageAspect?: SocialPostImageAspect;
  imageTextDensity?: SocialPostImageTextDensity;
  /** Logo de marca (data URL) para no perderlo al cambiar de paso / reabrir. */
  logoDataUrl?: string | null;
  /** Imágenes de contenido (slots), data URL o null. */
  contentImageDataUrls?: Array<string | null>;
  /** Referencias de línea gráfica / moodboard. */
  marketingLineImageDataUrls?: Array<string | null>;
  generatedContent?: PersistedGeneratedContent;
};

const AUTO_STRATEGY_TOPICS = [
  "Presentación de marca",
  "Beneficio principal",
  "Tip útil para tu audiencia",
  "Prueba social / confianza",
  "Promoción o llamada a la acción",
  "Detrás de escena",
  "Contenido de valor",
  "Cierre e invitación a conectar",
];

function buildAutoTopics(count: number): string[] {
  if (count <= 0) return [];
  return Array.from({ length: count }, (_, i) => {
    return AUTO_STRATEGY_TOPICS[i % AUTO_STRATEGY_TOPICS.length];
  });
}

const STORAGE_KEY_PROJECTS = (companyId: string) =>
  `wembii_socialAuto_projects_${companyId || "default"}`;
const STORAGE_KEY_DRAFT = (companyId: string, projectId: string) =>
  `wembii_socialAuto_draft_${companyId || "default"}_${projectId}`;
const STORAGE_KEY_MIGRATED = (companyId: string) =>
  `wembii_socialAuto_migrated_db_v1_${companyId || "default"}`;

function loadProjects(companyId: string | null): AutoPostProject[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PROJECTS(companyId || ""));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AutoPostProject[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveProjects(companyId: string | null, list: AutoPostProject[]) {
  try {
    localStorage.setItem(
      STORAGE_KEY_PROJECTS(companyId || ""),
      JSON.stringify(list)
    );
  } catch {
    // ignore quota / private
  }
}

function loadDraft(
  companyId: string | null,
  projectId: string
): AutoPostDraft | null {
  try {
    const raw = localStorage.getItem(
      STORAGE_KEY_DRAFT(companyId || "", projectId)
    );
    if (!raw) return null;
    return JSON.parse(raw) as AutoPostDraft;
  } catch {
    return null;
  }
}

function saveDraft(
  companyId: string | null,
  projectId: string,
  draft: AutoPostDraft
) {
  localStorage.setItem(
    STORAGE_KEY_DRAFT(companyId || "", projectId),
    JSON.stringify(draft)
  );
}

function draftHasGeneratedContent(draft: AutoPostDraft | null | undefined): boolean {
  if (!draft?.generatedContent) return false;
  const { texts, images } = draft.generatedContent;
  const hasRealText = Object.values(texts || {}).some(
    (t) => typeof t === "string" && t.trim() && !isPlaceholderCaption(t)
  );
  const hasImages = Object.keys(images || {}).length > 0;
  return hasRealText || hasImages;
}

function isDurableHttpUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== "string") return false;
  if (!/^https?:\/\//i.test(url)) return false;
  // OpenAI CDN links expire quickly and should not be persisted.
  if (url.includes("oaidalleapiprodscus") || url.includes("openai.com")) {
    return false;
  }
  return true;
}

async function previewUrlToDataUrl(url: string): Promise<string | null> {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const dataUrl = (await getBase64(blob)) as string;
    return dataUrl || null;
  } catch {
    return null;
  }
}

function persistedImageToUploadedImage(
  value: string,
  name: string
): UploadedImage | null {
  if (!value || typeof value !== "string") return null;
  if (isDurableHttpUrl(value)) {
    return {
      file: new File([], `${name}.jpg`, { type: "image/jpeg" }),
      previewUrl: value,
      durableUrl: value,
    };
  }
  if (!value.startsWith("data:")) return null;
  const mimeMatch = /^data:([^;]+);/.exec(value);
  const mime = mimeMatch?.[1] || "image/jpeg";
  const ext = mime.includes("png")
    ? "png"
    : mime.includes("webp")
    ? "webp"
    : "jpg";
  let file: File;
  try {
    const binary = atob(value.split(",")[1] || "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    file = new File([bytes], `${name}.${ext}`, { type: mime });
  } catch {
    file = new File([], `${name}.${ext}`, { type: mime });
  }
  return { file, previewUrl: value };
}

function dataUrlToUploadedImage(dataUrl: string, name: string): UploadedImage {
  return (
    persistedImageToUploadedImage(dataUrl, name) || {
      file: new File([], `${name}.jpg`, { type: "image/jpeg" }),
      previewUrl: dataUrl,
    }
  );
}

function restoreUploadSlots(
  dataUrls: Array<string | null> | undefined,
  slotCount: number,
  namePrefix: string
): Array<UploadedImage | null> {
  const out: Array<UploadedImage | null> = Array.from(
    { length: slotCount },
    () => null
  );
  if (!Array.isArray(dataUrls)) return out;
  dataUrls.slice(0, slotCount).forEach((value, idx) => {
    if (typeof value !== "string") return;
    const restored = persistedImageToUploadedImage(
      value,
      `${namePrefix}-${idx + 1}`
    );
    if (restored) out[idx] = restored;
  });
  return out;
}

function mapApiProject(row: {
  id: string;
  name: string;
  hasGeneratedContent?: boolean;
  createdAt?: string | number;
  updatedAt?: string | number;
}): AutoPostProject {
  const createdAt =
    typeof row.createdAt === "number"
      ? row.createdAt
      : row.createdAt
      ? new Date(row.createdAt).getTime()
      : Date.now();
  const updatedAt =
    typeof row.updatedAt === "number"
      ? row.updatedAt
      : row.updatedAt
      ? new Date(row.updatedAt).getTime()
      : createdAt;
  return {
    id: row.id,
    name: row.name,
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
    hasGeneratedContent: Boolean(row.hasGeneratedContent),
  };
}

async function persistAssetForCompany(
  companyId: string,
  img: UploadedImage | null | undefined,
  name = "asset"
): Promise<string | null> {
  if (!img) return null;
  if (isDurableHttpUrl(img.durableUrl)) return img.durableUrl || null;
  if (isDurableHttpUrl(img.previewUrl)) return img.previewUrl;
  try {
    if (img.file && img.file.size > 0) {
      return await socialAutoPostsApi.uploadFile(
        companyId,
        img.file,
        img.file.name || `${name}.png`
      );
    }
    if (img.previewUrl?.startsWith("data:")) {
      return await socialAutoPostsApi.uploadDataUrl(
        companyId,
        img.previewUrl,
        name
      );
    }
  } catch (error) {
    console.error("[SocialAutomatedPosts] asset upload failed", error);
  }
  return null;
}

async function hydrateDraftAssets(
  companyId: string,
  draft: AutoPostDraft
): Promise<AutoPostDraft> {
  const next: AutoPostDraft = { ...draft };
  if (typeof next.logoDataUrl === "string" && next.logoDataUrl.startsWith("data:")) {
    next.logoDataUrl =
      (await socialAutoPostsApi.uploadDataUrl(
        companyId,
        next.logoDataUrl,
        "logo"
      )) || null;
  }
  if (Array.isArray(next.contentImageDataUrls)) {
    next.contentImageDataUrls = await Promise.all(
      next.contentImageDataUrls.map(async (value, idx) => {
        if (typeof value === "string" && value.startsWith("data:")) {
          return (
            (await socialAutoPostsApi.uploadDataUrl(
              companyId,
              value,
              `content-${idx + 1}`
            )) || null
          );
        }
        return value;
      })
    );
  }
  if (Array.isArray(next.marketingLineImageDataUrls)) {
    next.marketingLineImageDataUrls = await Promise.all(
      next.marketingLineImageDataUrls.map(async (value, idx) => {
        if (typeof value === "string" && value.startsWith("data:")) {
          return (
            (await socialAutoPostsApi.uploadDataUrl(
              companyId,
              value,
              `moodboard-${idx + 1}`
            )) || null
          );
        }
        return value;
      })
    );
  }
  if (next.generatedContent?.images) {
    const images: Record<string, string> = {};
    await Promise.all(
      Object.entries(next.generatedContent.images).map(async ([id, value]) => {
        if (typeof value !== "string") return;
        if (isDurableHttpUrl(value)) {
          images[id] = value;
          return;
        }
        if (value.startsWith("data:")) {
          const url = await socialAutoPostsApi.uploadDataUrl(
            companyId,
            value,
            `post-${id}`
          );
          if (url) images[id] = url;
        }
      })
    );
    next.generatedContent = { ...next.generatedContent, images };
  }
  return next;
}

async function migrateLocalProjectsIfNeeded(companyId: string) {
  try {
    if (localStorage.getItem(STORAGE_KEY_MIGRATED(companyId))) return;
  } catch {
    return;
  }
  const local = loadProjects(companyId);
  if (!local.length) {
    try {
      localStorage.setItem(STORAGE_KEY_MIGRATED(companyId), "1");
    } catch {
      // ignore
    }
    return;
  }
  try {
    const remote = await socialAutoPostsApi.list(companyId);
    const remoteIds = new Set(remote.map((row) => row.id));
    for (const project of local) {
      if (remoteIds.has(project.id)) continue;
      const draft = loadDraft(companyId, project.id);
      const hydrated = draft
        ? await hydrateDraftAssets(companyId, draft)
        : undefined;
      await socialAutoPostsApi.upsert(companyId, {
        id: project.id,
        name: project.name,
        hasGeneratedContent:
          Boolean(project.hasGeneratedContent) ||
          draftHasGeneratedContent(draft),
        draft: hydrated,
      });
    }
    localStorage.setItem(STORAGE_KEY_MIGRATED(companyId), "1");
  } catch (error) {
    console.error("[SocialAutomatedPosts] localStorage migrate failed", error);
  }
}

type PreviewPost = {
  id: string;
  date: Date;
  time: string;
  title: string;
  text: string;
  image: string;
  confidence: number;
  contentImageIndex: number;
};

type SocialPreviewId = "instagram" | "facebook" | "linkedin" | "twitter" | "tiktok";

const SOCIAL_PREVIEWS: Array<{ id: SocialPreviewId; label: string }> = [
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
];

/** Nombre de red que espera el endpoint openai/posts (como en el planificador principal). */
const OPENAI_SOCIAL_NAME: Record<SocialPreviewId, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "Twitter",
  tiktok: "TikTok",
};

function SocialPreviewLogo({
  socialId,
  active,
}: {
  socialId: SocialPreviewId;
  active: boolean;
}) {
  const props = { active, size: "1x" as const };
  switch (socialId) {
    case "facebook":
      return <FacebookIcon {...props} />;
    case "instagram":
      return <InstagramIcon {...props} />;
    case "linkedin":
      return <LinkedInIcon {...props} />;
    case "twitter":
      return <TwitterIcon {...props} />;
    case "tiktok":
      return <TiktokIcon {...props} />;
    default:
      return null;
  }
}

type AutoPostSocialKey = "instagram" | "facebook" | "linkedIn" | "twitter" | "tiktok";

const AUTO_POST_REQUIRED_SOCIALS: Array<{
  previewId: SocialPreviewId;
  providerId: SocialProviderId;
  settingsKey: AutoPostSocialKey;
}> = [
  { previewId: "instagram", providerId: SocialProviderId.Instagram, settingsKey: "instagram" },
  { previewId: "facebook", providerId: SocialProviderId.Facebook, settingsKey: "facebook" },
  { previewId: "linkedin", providerId: SocialProviderId.LinkedIn, settingsKey: "linkedIn" },
  { previewId: "twitter", providerId: SocialProviderId.Twitter, settingsKey: "twitter" },
  { previewId: "tiktok", providerId: SocialProviderId.TikTok, settingsKey: "tiktok" },
];

const getAutoPostRequiredSocials = () =>
  AUTO_POST_REQUIRED_SOCIALS.filter(
    (s) => isTiktokPostingEnabled() || s.providerId !== SocialProviderId.TikTok
  );

/** Misma regla que Integraciones / Publicaciones: token o nombre, y Instagram Login ≡ Instagram. */
function hasSocialSettingData(setting: unknown): boolean {
  if (!setting || typeof setting !== "object") return false;
  const s = setting as Record<string, unknown>;
  if (!Object.keys(s).length) return false;
  return Boolean(s.token || s.name || s.userName || s.pageId || s.organizationId);
}

function isAutoPostSocialSynced(
  socialSettings: Record<string, unknown> | null | undefined,
  previewId: SocialPreviewId
): boolean {
  if (!socialSettings) return false;
  if (previewId === "instagram") {
    return isInstagramSocialConnected(socialSettings);
  }
  if (previewId === "linkedin") {
    return (
      hasSocialSettingData(socialSettings.linkedIn) ||
      hasSocialSettingData(socialSettings.linkedInBusiness)
    );
  }
  const keyByPreview: Record<string, string> = {
    facebook: "facebook",
    twitter: "twitter",
    tiktok: "tiktok",
  };
  const key = keyByPreview[previewId];
  return key ? hasSocialSettingData(socialSettings[key]) : false;
}

/** Instagram vía Login (sin Facebook) debe usar el endpoint de login. */
function shouldPostInstagramViaLogin(
  socialSettings: Record<string, unknown> | null | undefined
): boolean {
  if (!socialSettings) return false;
  const viaFacebook = socialSettings.instagram as Record<string, unknown> | undefined;
  if (hasSocialSettingData(viaFacebook)) return false;
  return hasSocialSettingData(socialSettings.instagramLogin);
}

const INDUSTRIES: Option[] = [
  { value: "ecommerce", label: "E-commerce" },
  { value: "salud", label: "Salud" },
  { value: "educacion", label: "Educación" },
  { value: "bienes-raices", label: "Bienes raíces" },
  { value: "servicios", label: "Servicios" },
  { value: "tecnologia", label: "Tecnología" },
  { value: "otros", label: "Otros" },
];

function getIndustryLabel(industry: string): string {
  return INDUSTRIES.find((item) => item.value === industry)?.label || industry || "Tu industria";
}

type CaptionBrandContext = {
  industryLabel: string;
  brandName: string;
  brandContext: string;
  themeForIndex: (index: number) => string;
};

/** Contexto de marca aislado por cliente (usa nombre de empresa, no del proyecto IA). */
function buildCaptionBrandContext(args: {
  configMode: ConfigMode;
  industry: string;
  brandDescription: string;
  customContentBrief: string;
  additionalInfo: string;
  companyName: string;
  topics: Option[];
  customPostTopics: string[];
  previewDatesLength: number;
  customIndustryFallback: string;
  autoIndustryFallback: string;
}): CaptionBrandContext {
  const brandName = (args.companyName || "").trim();
  const brief = args.customContentBrief.trim();
  const brandDesc = args.brandDescription.trim();
  const notes = args.additionalInfo.trim();

  if (args.configMode === "custom") {
    const industryLabel =
      brief.slice(0, 120) ||
      brandName ||
      args.customIndustryFallback;
    const brandContext = [
      brandName ? `Nombre de la empresa/marca: ${brandName}.` : "",
      brief ? `Brief del contenido: ${brief}` : "",
      notes ? `Notas extra: ${notes}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    return {
      industryLabel,
      brandName,
      brandContext,
      themeForIndex: (index) => {
        const topic = args.customPostTopics[index]?.trim();
        if (topic) return topic;
        if (args.customPostTopics.length) {
          return (
            args.customPostTopics[index % args.customPostTopics.length]?.trim() ||
            "General"
          );
        }
        return "General";
      },
    };
  }

  if (args.configMode === "auto") {
    const industryLabel =
      brandDesc.slice(0, 120) || brandName || args.autoIndustryFallback;
    const brandContext = [
      brandName ? `Nombre de la empresa/marca: ${brandName}.` : "",
      brandDesc ? `Descripción de marca: ${brandDesc}` : "",
      notes ? `Notas extra: ${notes}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    const autoTopics = buildAutoTopics(Math.max(args.previewDatesLength, 1));
    return {
      industryLabel,
      brandName,
      brandContext,
      themeForIndex: (index) =>
        autoTopics.length
          ? autoTopics[index % autoTopics.length]
          : "General",
    };
  }

  const industryLabel = getIndustryLabel(args.industry);
  const brandContext = [
    brandName ? `Nombre de la empresa/marca: ${brandName}.` : "",
    `Industria seleccionada: ${industryLabel}.`,
    notes ? `Notas extra: ${notes}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return {
    industryLabel,
    brandName,
    brandContext,
    themeForIndex: (index) =>
      args.topics.length
        ? args.topics[index % args.topics.length].label
        : "General",
  };
}

// Ideas de etiquetas para arrancar el MVP (luego se reemplaza por catálogo real).
const TOPIC_TAGS: Option[] = [
  { value: "promociones", label: "Promociones" },
  { value: "lanzamientos", label: "Lanzamientos" },
  { value: "tips", label: "Tips" },
  { value: "beneficios", label: "Beneficios" },
  { value: "casos", label: "Casos / Testimonios" },
  { value: "tutoriales", label: "Tutoriales" },
  { value: "eventos", label: "Eventos" },
  { value: "novedades", label: "Novedades" },
  { value: "cocina", label: "Lifestyle / Cocina" },
  { value: "moda", label: "Moda" },
  { value: "finanzas", label: "Finanzas" },
  { value: "bienestar", label: "Bienestar" },
];

const DAYS = [
  { key: "0", label: "Dom" },
  { key: "1", label: "Lun" },
  { key: "2", label: "Mar" },
  { key: "3", label: "Mié" },
  { key: "4", label: "Jue" },
  { key: "5", label: "Vie" },
  { key: "6", label: "Sáb" },
] as const;

const MAX_POSTS_PER_MONTH = 8;
const MAX_POSTS_PER_WEEK = 2;

// Duración del plan en semanas (por defecto 4 = un mes).
const DURATION_OPTIONS = [
  { value: 1, label: "1 semana" },
  { value: 2, label: "2 semanas" },
  { value: 3, label: "3 semanas" },
  { value: 4, label: "1 mes (4 semanas)" },
] as const;

const DEFAULT_DURATION_WEEKS = 4;

const CONTENT_TYPE_OPTIONS: Array<{
  value: AutoPostContentType;
  label: string;
  hintKey: string;
  hintDefault: string;
}> = [
  {
    value: "both",
    label: "Texto y diseño",
    hintKey: "app.socialAutomatedPosts.form.contentType.bothHint",
    hintDefault: "Genera imagen y texto. Consume 2 créditos por publicación.",
  },
  {
    value: "design",
    label: "Solo diseño",
    hintKey: "app.socialAutomatedPosts.form.contentType.designHint",
    hintDefault: "Genera únicamente la pieza gráfica. Consume 1 crédito.",
  },
  {
    value: "text",
    label: "Solo texto",
    hintKey: "app.socialAutomatedPosts.form.contentType.textHint",
    hintDefault: "Genera únicamente el texto. Consume 1 crédito.",
  },
];

const IMAGE_ASPECT_OPTIONS: Array<{
  value: SocialPostImageAspect;
  label: string;
  hint: string;
}> = [
  {
    value: "1:1",
    label: "Cuadrada 1:1",
    hint: "Formato clásico de feed.",
  },
  {
    value: "2:3",
    label: "Vertical 2:3",
    hint: "Más altura, ideal para feed vertical.",
  },
];

const IMAGE_TEXT_DENSITY_OPTIONS: Array<{
  value: SocialPostImageTextDensity;
  label: string;
  hint: string;
}> = [
  {
    value: "minimal",
    label: "Poco texto",
    hint: "Titular corto y diseño limpio.",
  },
  {
    value: "rich",
    label: "Más texto",
    hint: "Titular + beneficio + CTA.",
  },
];

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, "0");
  const dd = `${date.getDate()}`.padStart(2, "0");
  return `${dd}/${mm}/${yyyy}`;
}

function getTodayDate(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function buildScheduleDate(date: Date, time: string): string {
  return scheduleDateStringForApiFromDateAndHour(date, time);
}

function mulberry32(seed: number) {
  let t = seed;
  return () => {
    t += 0x6d2b79f5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function isPlaceholderCaption(text: string): boolean {
  const t = text.trim();
  return (
    t.startsWith("Se generará con ChatGPT") ||
    t.startsWith("Estamos generando el contenido con la IA de Wembii")
  );
}

/** Caption realista (estilo redes) para openai/posts. */
function buildChatGptCaptionAdditionalInfo(
  industryLabel: string,
  themeLabel: string,
  opts?: {
    currentText?: string;
    changeRequest?: string;
    brandName?: string;
    brandContext?: string;
  }
): string {
  const changeRequest = (opts?.changeRequest || "").trim();
  const currentText = (opts?.currentText || "").trim();
  const brandName = (opts?.brandName || "").trim();
  const brandContext = (opts?.brandContext || "").trim();

  const identityRules = [
    brandName ? `Empresa/marca: "${brandName}".` : "",
    `Identidad/rubro de ESTE cliente: "${industryLabel}".`,
    brandContext ? `Contexto de marca a respetar: ${brandContext}` : "",
    `Usa el nombre comercial de la empresa cuando menciones la marca. Nunca uses el nombre interno del proyecto IA ni IDs.`,
    `Habla ÚNICAMENTE de este negocio y de la temática indicada. No inventes otro rubro (moda, retail, etc.) ni copies identidad de otros clientes.`,
    `No uses placeholders genéricos como #TuIndustria, #TuMarca, [industria] o similares.`,
    `Los hashtags deben ser relevantes al rubro y al tema reales de este cliente.`,
  ]
    .filter(Boolean)
    .join(" ");

  if (changeRequest && currentText) {
    return [
      identityRules,
      `Tema del post: "${themeLabel}".`,
      `Texto actual del post (caption):`,
      `"""${currentText}"""`,
      `El usuario pidió estos cambios: "${changeRequest}".`,
      `Reescribí SOLO el caption completo ya corregido, listo para publicar.`,
      `Mantené el idioma español, tono de marca en redes, emojis y hashtags útiles si encajan.`,
      `No expliques los cambios; devolvé únicamente el nuevo texto del post.`,
      `No menciones ChatGPT, OpenAI ni que es contenido generado por IA.`,
    ].join(" ");
  }

  return [
    identityRules,
    `Tema específico de esta publicación: "${themeLabel}".`,
    `Tu tarea: escribir SOLO el pie de publicación (caption) listo para publicar en la red social indicada.`,
    `Estilo: como un posteo real de esta marca en redes sociales, en español natural, cercano y persuasivo.`,
    `Incluí emojis relevantes (2 a 5) y 2 a 4 hashtags útiles al final (sin placeholders).`,
    `Estructura sugerida: gancho con emoji → 1 o 2 frases de beneficio concretas de este negocio → CTA claro → hashtags.`,
    `Longitud: 3 a 6 líneas cortas; no suene genérico ni robótico; no menciones ChatGPT, OpenAI ni que es contenido generado por IA.`,
  ].join(" ");
}

function formatSmartHourLabel(hour: number, minutes: number): string {
  const m = `${minutes}`.padStart(2, "0");
  const period = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${`${h12}`.padStart(2, "0")}:${m} ${period}`;
}

function engagementFromScore(score: number): string {
  const map: Record<number, string> = {
    1: "+8%",
    2: "+14%",
    3: "+21%",
    4: "+27%",
    5: "+32%",
  };
  return map[score] || "+8%";
}

function smartTimeToKey(hour: number, minutes: number): string {
  return `${`${hour}`.padStart(2, "0")}:${`${minutes}`.padStart(2, "0")}`;
}

export default function SocialAutomatedPosts() {
  const intl = useIntl();
  const { goToSocialCalendar, goToSocialAutomatedPosts } = useRouterCore();
  const { companyId, company, companyPlan, socialSettings, getMeSocialSettings } =
    useMeCore();
  const companyName = (company?.name || "").trim();
  const isPremiumPlan =
    (companyPlan?.name ?? ".").split(".")[2] === "Premium";

  // Proyectos: listado y el que está abierto (null = pantalla listado).
  const [projects, setProjects] = useState<AutoPostProject[]>(() =>
    loadProjects(companyId)
  );
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentProjectName, setCurrentProjectName] = useState<string>("");
  const openProjectRequestRef = useRef(0);
  const persistReadyRef = useRef(false);

  // Sincronizar lista de proyectos desde la DB (con cache local mientras carga).
  React.useEffect(() => {
    if (!companyId) {
      setProjects([]);
      setCurrentProjectId(null);
      return;
    }
    const cached = loadProjects(companyId);
    setProjects(cached);
    setCurrentProjectId((prev) => {
      if (!prev) return null;
      return cached.some((p) => p.id === prev) ? prev : null;
    });
    let cancelled = false;
    const loadRemote = async () => {
      await migrateLocalProjectsIfNeeded(companyId);
      if (cancelled) return;
      try {
        const remote = await socialAutoPostsApi.list(companyId);
        if (cancelled) return;
        const list = remote.map(mapApiProject);
        setProjects(list);
        saveProjects(companyId, list);
        setCurrentProjectId((prev) => {
          if (!prev) return null;
          return list.some((p) => p.id === prev) ? prev : null;
        });
      } catch (error) {
        console.error("[SocialAutomatedPosts] list projects failed", error);
      }
    };
    void loadRemote();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Step control (dentro del wizard de un proyecto).
  const [step, setStep] = useState<StepId>("config");

  // Config step
  const [configMode, setConfigMode] = useState<ConfigMode>("guided");
  const [industry, setIndustry] = useState<string>("");
  const [topics, setTopics] = useState<Option[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(getTodayDate);
  const [additionalInfo, setAdditionalInfo] = useState<string>("");
  const [customContentBrief, setCustomContentBrief] = useState<string>("");
  const [customPostTopics, setCustomPostTopics] = useState<string[]>([]);
  const [topicIdeasByIndex, setTopicIdeasByIndex] = useState<
    Record<number, string[]>
  >({});
  const [topicIdeasOpenIndex, setTopicIdeasOpenIndex] = useState<number | null>(
    null
  );
  const [topicIdeasLoadingIndex, setTopicIdeasLoadingIndex] = useState<
    number | null
  >(null);
  const [brandDescription, setBrandDescription] = useState<string>("");
  const [includeLogo, setIncludeLogo] = useState(true);
  const [imageAspect, setImageAspect] =
    useState<SocialPostImageAspect>("1:1");
  const [imageTextDensity, setImageTextDensity] =
    useState<SocialPostImageTextDensity>("minimal");
  const [contentType, setContentType] = useState<AutoPostContentType>("both");
  const [durationWeeks, setDurationWeeks] = useState<number>(
    DEFAULT_DURATION_WEEKS
  );

  // Media step
  const [logo, setLogo] = useState<UploadedImage | null>(null);
  const MAX_CONTENT_IMAGES = 5;
  const MAX_MARKETING_LINE_IMAGES = 3;
  const MIN_CONTENT_IMAGES = 3;
  const [images, setImages] = useState<(UploadedImage | null)[]>(
    Array.from({ length: 5 }).map(() => null)
  );
  const [marketingLineImages, setMarketingLineImages] = useState<
    (UploadedImage | null)[]
  >(Array.from({ length: 3 }).map(() => null));
  const [isDraggingMarketingLine, setIsDraggingMarketingLine] =
    useState(false);

  const [replaceSlotIndex, setReplaceSlotIndex] = useState<number | null>(
    null
  );
  const [isDraggingImages, setIsDraggingImages] = useState(false);

  // Preview step
  const [previewSeed, setPreviewSeed] = useState<number>(() => Date.now());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isGenerationConfirmOpen, setIsGenerationConfirmOpen] = useState(false);
  const [activePreviewPost, setActivePreviewPost] = useState<
    PreviewPost | null
  >(null);
  const [activeSocialPreview, setActiveSocialPreview] =
    useState<SocialPreviewId>("instagram");

  // NanoBanana image edits, per-post (so each day can reach its "ideal" image).
  const [postImageOverrides, setPostImageOverrides] = useState<
    Record<string, UploadedImage>
  >({});
  const [postTextOverrides, setPostTextOverrides] = useState<
    Record<string, string>
  >({});
  const [postScheduleOverrides, setPostScheduleOverrides] = useState<
    Record<string, { date: Date; time: string }>
  >({});
  const [postTextDraft, setPostTextDraft] = useState("");
  const [textChangePrompt, setTextChangePrompt] = useState("");
  const [nanoEditPrompt, setNanoEditPrompt] = useState("");
  const [isEditingNanoBanana, setIsEditingNanoBanana] = useState(false);
  const [editingImagePostIds, setEditingImagePostIds] = useState<
    Record<string, boolean>
  >({});
  const [isGeneratingPostText, setIsGeneratingPostText] = useState(false);
  const [nanoEditProgress, setNanoEditProgress] = useState(0);
  const [showBackgroundWaitTip, setShowBackgroundWaitTip] = useState(false);
  const [editReferenceImages, setEditReferenceImages] = useState<
    UploadedImage[]
  >([]);
  const modalImageInputRef = useRef<HTMLInputElement>(null);
  const editRefInputRef = useRef<HTMLInputElement>(null);

  // Estado del job de generación en background (sobrevive a la navegación).
  const [jobState, setJobState] = useState<AutoPostJobState | null>(null);
  const [genNow, setGenNow] = useState<number>(() => Date.now());

  const jobKey = useMemo(
    () => (companyId && currentProjectId ? `${companyId}:${currentProjectId}` : ""),
    [companyId, currentProjectId]
  );

  const isGeneratingContent = jobState?.status === "running";
  const isEditingActiveImage = Boolean(
    activePreviewPost && editingImagePostIds[activePreviewPost.id]
  );
  const isBusyWithAiTask =
    isGeneratingContent ||
    isEditingActiveImage ||
    isEditingNanoBanana ||
    isGeneratingPostText;

  const [approvalMode, setApprovalMode] = useState(false);
  const [approvedPostIds, setApprovedPostIds] = useState<Record<string, boolean>>(
    {}
  );
  const [scheduledPostIds, setScheduledPostIds] = useState<Record<string, boolean>>(
    {}
  );

  const [syncAccountsModalOpen, setSyncAccountsModalOpen] = useState(false);
  const [scheduleNetworksModalOpen, setScheduleNetworksModalOpen] =
    useState(false);
  const [selectedScheduleNetworks, setSelectedScheduleNetworks] = useState<
    Record<string, boolean>
  >({});
  /** Redes elegibles al abrir el modal (cálculo fresco, no depende del render stale). */
  const [scheduleEligibleNetworks, setScheduleEligibleNetworks] = useState<
    SocialPreviewId[]
  >([]);
  const [scheduleSuccessModalOpen, setScheduleSuccessModalOpen] =
    useState(false);
  const [scheduleSuccessStats, setScheduleSuccessStats] = useState<{
    sent: number;
    failed: number;
    networks: string[];
  } | null>(null);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [smartScheduleOpen, setSmartScheduleOpen] = useState(false);
  const [showAllSmartHours, setShowAllSmartHours] = useState(false);
  const [selectedSmartTime, setSelectedSmartTime] = useState<string | null>(null);
  const [calendarMonthOffset, setCalendarMonthOffset] = useState(0);

  // Horarios sugeridos (misma fuente que el posteador), según día de inicio.
  const smartScheduleSuggestions = useMemo(() => {
    const dayKey = `${(startDate ?? getTodayDate()).getDay()}`;
    const times = SMART_TIMES[dayKey] || SMART_TIMES["1"] || [];
    const mapped = times.map((t) => ({
      time: smartTimeToKey(t.hour, t.minutes),
      label: formatSmartHourLabel(t.hour, t.minutes),
      engagement: engagementFromScore(t.score),
      stars: Math.max(1, Math.min(5, t.score)),
      score: t.score,
      sortMinutes: t.hour * 60 + t.minutes,
    }));
    if (showAllSmartHours) {
      return [...mapped].sort((a, b) => a.sortMinutes - b.sortMinutes);
    }
    return [...mapped].sort((a, b) => b.score - a.score).slice(0, 5);
  }, [startDate, showAllSmartHours]);

  const hasGeneratedContent = useMemo(() => {
    const fromOverrides =
      Object.keys(postImageOverrides).length > 0 ||
      Object.values(postTextOverrides).some(
        (t) => typeof t === "string" && t.trim() && !isPlaceholderCaption(t)
      );
    if (fromOverrides) return true;
    if (!jobState) return false;
    return (
      jobState.status === "done" ||
      jobState.status === "stopped" ||
      jobState.done > 0 ||
      Object.keys(jobState.results || {}).length > 0
    );
  }, [jobState, postImageOverrides, postTextOverrides]);

  // Crear proyecto: nombre temporal antes de crear.
  const [newProjectName, setNewProjectName] = useState("");

  const missingRequiredSocials = useMemo(() => {
    if (!socialSettings) {
      return getAutoPostRequiredSocials().map((s) => s.previewId);
    }

    const out: SocialPreviewId[] = [];
    getAutoPostRequiredSocials().forEach((s) => {
      if (
        !isAutoPostSocialSynced(
          socialSettings as Record<string, unknown>,
          s.previewId
        )
      ) {
        out.push(s.previewId);
      }
    });
    return out;
  }, [socialSettings]);

  useEffect(() => {
    if (!scheduleNetworksModalOpen) return;
    // Si Redux ya se actualizó, unir elegibles sin borrar las del fetch fresco.
    const fromStore = getAutoPostRequiredSocials()
      .filter((s) =>
        isAutoPostSocialSynced(
          socialSettings as Record<string, unknown>,
          s.previewId
        )
      )
      .map((s) => s.previewId);
    setScheduleEligibleNetworks((prev) => {
      const merged = Array.from(new Set([...prev, ...fromStore]));
      return merged.length ? merged : fromStore;
    });
  }, [scheduleNetworksModalOpen, socialSettings]);

  useEffect(() => {
    if (!scheduleNetworksModalOpen || !scheduleEligibleNetworks.length) return;
    setSelectedScheduleNetworks((prev) => {
      const next: Record<string, boolean> = {};
      scheduleEligibleNetworks.forEach((id) => {
        next[id] = prev[id] === undefined ? true : Boolean(prev[id]);
      });
      return next;
    });
  }, [scheduleNetworksModalOpen, scheduleEligibleNetworks]);

  const hasAnySocialSynced = useMemo(
    () => missingRequiredSocials.length < getAutoPostRequiredSocials().length,
    [missingRequiredSocials.length]
  );

  /** Por defecto usa la key global de Wembii; key propia solo si está sincronizada. */
  const hasOpenAiConfigured = useMemo(
    () =>
      Boolean(socialSettings?.openai?.name) ||
      socialSettings?.openai?.managed === true ||
      // Si no hay integración OpenAI propia, el BE usa OPENAI_KEY (Wembii).
      !socialSettings?.openai,
    [socialSettings]
  );

  // Mantiene una red válida como pestaña activa al abrir el detalle.
  useEffect(() => {
    if (!isPostModalOpen) return;
    if (SOCIAL_PREVIEWS.some((s) => s.id === activeSocialPreview)) return;
    setActiveSocialPreview(SOCIAL_PREVIEWS[0].id);
  }, [isPostModalOpen, activeSocialPreview]);

  useEffect(() => {
    setPostTextDraft(activePreviewPost?.text ?? "");
    setTextChangePrompt("");
  }, [activePreviewPost]);

  const refreshSocialSettings = useCallback(() => {
    if (companyId) getMeSocialSettings(companyId);
  }, [companyId, getMeSocialSettings]);

  const closePostModal = useCallback(() => {
    const editingInBackground = Boolean(
      activePreviewPost && editingImagePostIds[activePreviewPost.id]
    );
    setIsPostModalOpen(false);
    setActivePreviewPost(null);
    setNanoEditPrompt("");
    setTextChangePrompt("");
    setNanoEditProgress(0);
    setIsGeneratingPostText(false);
    setEditReferenceImages((prev) => {
      prev.forEach((img) => {
        if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
      });
      return [];
    });
    // No cancelamos la edición de imagen: sigue en segundo plano.
    if (editingInBackground) {
      toast.info(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.background.imageEditContinue",
          defaultMessage:
            "Seguimos aplicando los cambios del diseño en segundo plano. Podés cerrar y volver cuando quieras.",
        })
      );
    }
  }, [activePreviewPost, editingImagePostIds, intl]);

  // Si una tarea de IA demora, avisar que pueden cerrar y continuar en background.
  useEffect(() => {
    if (!isBusyWithAiTask) {
      setShowBackgroundWaitTip(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowBackgroundWaitTip(true);
      toast.info(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.background.waitTipToast",
          defaultMessage:
            "Esto está tardando un poco. Podés cerrar la ventana: los cambios siguen en segundo plano.",
        }),
        { toastId: "social-auto-bg-wait-tip", autoClose: 8000 }
      );
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [isBusyWithAiTask, intl]);

  // Progreso visual mientras se edita la imagen del post activo.
  useEffect(() => {
    if (!isEditingActiveImage) return;
    setNanoEditProgress((p) => (p < 12 ? 12 : p));
    const timer = window.setInterval(() => {
      setNanoEditProgress((p) => {
        if (p >= 90) return p;
        return Math.min(90, p + Math.floor(3 + Math.random() * 8));
      });
    }, 1200);
    return () => window.clearInterval(timer);
  }, [isEditingActiveImage]);

  // Persistir borrador del proyecto actual (config + paso + contenido generado).
  const persistCurrentDraft = useCallback(async () => {
    if (!currentProjectId || !companyId || !persistReadyRef.current) return;

    const schedules: PersistedGeneratedContent["schedules"] = {};
    Object.entries(postScheduleOverrides).forEach(([id, value]) => {
      schedules![id] = {
        dateIso: value.date.toISOString(),
        time: value.time,
      };
    });

    const generatedImages: Record<string, string> = {};
    await Promise.all(
      Object.entries(postImageOverrides).map(async ([id, img]) => {
        const url = await persistAssetForCompany(companyId, img, `post-${id}`);
        if (url) generatedImages[id] = url;
      })
    );
    const texts = { ...postTextOverrides };
    const scheduledIds = Object.keys(scheduledPostIds).filter(
      (id) => scheduledPostIds[id]
    );

    const [logoDataUrl, contentImageDataUrls, marketingLineImageDataUrls] =
      await Promise.all([
        persistAssetForCompany(companyId, logo, "logo"),
        Promise.all(
          images.map((img, idx) =>
            persistAssetForCompany(companyId, img, `content-${idx + 1}`)
          )
        ),
        Promise.all(
          marketingLineImages.map((img, idx) =>
            persistAssetForCompany(companyId, img, `moodboard-${idx + 1}`)
          )
        ),
      ]);

    const previousDraft = loadDraft(companyId, currentProjectId);
    const previousImages = previousDraft?.generatedContent?.images || {};
    const imagesForSave =
      Object.keys(generatedImages).length > 0
        ? generatedImages
        : previousImages;
    const hasContent =
      Object.keys(imagesForSave).length > 0 ||
      Object.values(texts).some(
        (t) => typeof t === "string" && t.trim() && !isPlaceholderCaption(t)
      ) ||
      scheduledIds.length > 0;

    const payload: AutoPostDraft = {
      step: hasContent ? "preview" : step,
      industry,
      topics,
      selectedDays,
      startDateIso: startDate ? startDate.toISOString() : null,
      additionalInfo,
      contentType,
      durationWeeks,
      configMode,
      customContentBrief,
      customPostTopics,
      brandDescription,
      includeLogo,
      imageAspect,
      imageTextDensity,
      logoDataUrl,
      contentImageDataUrls,
      marketingLineImageDataUrls,
      generatedContent: hasContent
        ? {
            texts,
            images: imagesForSave,
            schedules,
            selectedSmartTime,
            previewSeed,
            scheduledPostIds: scheduledIds,
          }
        : undefined,
    };

    try {
      saveDraft(companyId, currentProjectId, payload);
    } catch {
      // ignore quota — la fuente de verdad ahora es la DB
    }

    try {
      await socialAutoPostsApi.upsert(companyId, {
        id: currentProjectId,
        name: currentProjectName || undefined,
        hasGeneratedContent: hasContent,
        draft: payload,
      });
    } catch (error) {
      console.error("[SocialAutomatedPosts] persist draft failed", error);
    }

    setProjects((prev) => {
      const list = prev.map((p) =>
        p.id === currentProjectId
          ? {
              ...p,
              updatedAt: Date.now(),
              hasGeneratedContent: hasContent || p.hasGeneratedContent,
            }
          : p
      );
      saveProjects(companyId, list);
      return list;
    });
  }, [
    companyId,
    currentProjectId,
    step,
    industry,
    topics,
    selectedDays,
    startDate,
    additionalInfo,
    contentType,
    durationWeeks,
    configMode,
    customContentBrief,
    customPostTopics,
    brandDescription,
    includeLogo,
    imageAspect,
    imageTextDensity,
    logo,
    images,
    marketingLineImages,
    postImageOverrides,
    postTextOverrides,
    postScheduleOverrides,
    selectedSmartTime,
    previewSeed,
    scheduledPostIds,
    currentProjectName,
  ]);

  const backToProjectList = useCallback(() => {
    void persistCurrentDraft().then(() => {
      setCurrentProjectId(null);
      setCurrentProjectName("");
      setStep("config");
    });
  }, [persistCurrentDraft]);

  const createProject = useCallback(
    (name: string) => {
      const id = `proj_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const now = Date.now();
      const newProject: AutoPostProject = {
        id,
        name: name.trim() || intl.formatMessage({ id: "app.socialAutomatedPosts.project.unnamed", defaultMessage: "Sin nombre" }),
        createdAt: now,
        updatedAt: now,
      };
      setProjects((prev) => {
        const next = [...prev, newProject];
        saveProjects(companyId, next);
        return next;
      });
      setCurrentProjectId(id);
      setCurrentProjectName(newProject.name);
      setStep("config");
      setConfigMode("guided");
      setIndustry("");
      setTopics([]);
      setSelectedDays([]);
      setStartDate(getTodayDate());
      setAdditionalInfo("");
      setCustomContentBrief("");
      setCustomPostTopics([]);
      setBrandDescription("");
      setIncludeLogo(true);
      setImageAspect("1:1");
      setImageTextDensity("minimal");
      setContentType("both");
      setDurationWeeks(DEFAULT_DURATION_WEEKS);
      setLogo(null);
      setImages(Array.from({ length: 5 }).map(() => null));
      setMarketingLineImages(Array.from({ length: 3 }).map(() => null));
      setPostImageOverrides({});
      setPostTextOverrides({});
      setApprovalMode(false);
      setApprovedPostIds({});
      setScheduledPostIds({});
      persistReadyRef.current = true;
      if (companyId) {
        void socialAutoPostsApi
          .upsert(companyId, {
            id,
            name: newProject.name,
            hasGeneratedContent: false,
            draft: {
              step: "config",
              industry: "",
              topics: [],
              selectedDays: [],
              startDateIso: getTodayDate().toISOString(),
              contentType: "both",
              durationWeeks: DEFAULT_DURATION_WEEKS,
              configMode: "guided",
            },
          })
          .catch((error) => {
            console.error(
              "[SocialAutomatedPosts] create project failed",
              error
            );
          });
      }
    },
    [companyId, intl]
  );

  const openProject = useCallback(
    (project: AutoPostProject, options?: { skipRemote?: boolean }) => {
      const skipRemote = Boolean(options?.skipRemote);
      const requestId = ++openProjectRequestRef.current;
      persistReadyRef.current = false;
      setCurrentProjectId(project.id);
      setCurrentProjectName(project.name);
      const draft = loadDraft(companyId, project.id);
      const liveJob = companyId
        ? autoPostGenerationManager.getState(`${companyId}:${project.id}`)
        : null;
      const liveHasContent = Boolean(
        liveJob &&
          (liveJob.done > 0 ||
            Object.values(liveJob.results || {}).some(
              (r) => Boolean(r.image) || Boolean(r.text)
            ))
      );
      const persistedHasContent = draftHasGeneratedContent(draft);

      if (draft) {
        setIndustry(draft.industry);
        setTopics(draft.topics);
        setSelectedDays(draft.selectedDays);
        setStartDate(
          draft.startDateIso ? new Date(draft.startDateIso) : getTodayDate()
        );
        setAdditionalInfo(draft.additionalInfo ?? "");
        setContentType(draft.contentType ?? "both");
        setDurationWeeks(draft.durationWeeks ?? DEFAULT_DURATION_WEEKS);
        setConfigMode(draft.configMode ?? "guided");
        setCustomContentBrief(draft.customContentBrief ?? "");
        setCustomPostTopics(
          Array.isArray(draft.customPostTopics) ? draft.customPostTopics : []
        );
        setBrandDescription(draft.brandDescription ?? "");
        setIncludeLogo(draft.includeLogo !== false);
        setImageAspect(
          String(draft.imageAspect) === "2:3" ||
            String(draft.imageAspect) === "3:4"
            ? "2:3"
            : "1:1"
        );
        setImageTextDensity(
          draft.imageTextDensity === "rich" ||
            draft.imageTextDensity === "minimal"
            ? draft.imageTextDensity
            : "minimal"
        );
        if (draft.logoDataUrl) {
          setLogo(dataUrlToUploadedImage(draft.logoDataUrl, "logo"));
        } else {
          setLogo(null);
        }
        setImages(
          restoreUploadSlots(draft.contentImageDataUrls, 5, "content")
        );
        setMarketingLineImages(
          restoreUploadSlots(draft.marketingLineImageDataUrls, 3, "moodboard")
        );
        if (draft.generatedContent?.selectedSmartTime != null) {
          setSelectedSmartTime(draft.generatedContent.selectedSmartTime);
        }
        if (draft.generatedContent?.previewSeed != null) {
          setPreviewSeed(draft.generatedContent.previewSeed);
        }
      } else {
        setIndustry("");
        setTopics([]);
        setSelectedDays([]);
        setStartDate(getTodayDate());
        setAdditionalInfo("");
        setContentType("both");
        setDurationWeeks(DEFAULT_DURATION_WEEKS);
        setConfigMode("guided");
        setCustomContentBrief("");
        setCustomPostTopics([]);
        setBrandDescription("");
        setIncludeLogo(true);
        setImageAspect("1:1");
        setImageTextDensity("minimal");
        setLogo(null);
        setImages(Array.from({ length: 5 }).map(() => null));
        setMarketingLineImages(Array.from({ length: 3 }).map(() => null));
      }

      if (draft?.generatedContent) {
        const texts = draft.generatedContent.texts || {};
        const imageMap: Record<string, UploadedImage> = {};
        Object.entries(draft.generatedContent.images || {}).forEach(
          ([id, value]) => {
            const restored = persistedImageToUploadedImage(
              value,
              `post-${id}`
            );
            if (restored) imageMap[id] = restored;
          }
        );
        setPostTextOverrides(texts);
        setPostImageOverrides(imageMap);
        const scheduleMap: Record<string, { date: Date; time: string }> = {};
        Object.entries(draft.generatedContent.schedules || {}).forEach(
          ([id, s]) => {
            scheduleMap[id] = {
              date: new Date(s.dateIso),
              time: s.time,
            };
          }
        );
        setPostScheduleOverrides(scheduleMap);
        const scheduledMap: Record<string, boolean> = {};
        (draft.generatedContent.scheduledPostIds || []).forEach((id) => {
          scheduledMap[id] = true;
        });
        setScheduledPostIds(scheduledMap);
      } else {
        setPostImageOverrides({});
        setPostTextOverrides({});
        setPostScheduleOverrides({});
        setScheduledPostIds({});
      }

      setApprovalMode(false);
      setApprovedPostIds({});

      // Si ya hay contenido (persistido o en memoria), ir directo a vista previa.
      if (persistedHasContent || liveHasContent || project.hasGeneratedContent) {
        setStep("preview");
      } else if (draft?.step) {
        setStep(draft.step);
      } else {
        setStep("config");
      }

      if (skipRemote || !companyId) {
        persistReadyRef.current = true;
        return;
      }

      void socialAutoPostsApi
        .get(companyId, project.id)
        .then((remote) => {
          if (openProjectRequestRef.current !== requestId) return;
          if (remote?.name) setCurrentProjectName(remote.name);
          const remoteDraft = (remote?.draft || null) as AutoPostDraft | null;
          if (remoteDraft) {
            try {
              saveDraft(companyId, project.id, remoteDraft);
            } catch {
              // ignore
            }
            openProject(
              {
                ...project,
                name: remote.name || project.name,
                hasGeneratedContent:
                  Boolean(remote.hasGeneratedContent) ||
                  project.hasGeneratedContent,
              },
              { skipRemote: true }
            );
            return;
          }
          persistReadyRef.current = true;
        })
        .catch((error) => {
          if (openProjectRequestRef.current !== requestId) return;
          console.error("[SocialAutomatedPosts] get project failed", error);
          persistReadyRef.current = true;
        });
    },
    [companyId]
  );

  const deleteProject = useCallback(
    (projectId: string, projectName: string) => {
      const message = intl.formatMessage(
        {
          id: "app.socialAutomatedPosts.projects.deleteConfirm",
          defaultMessage: '¿Eliminar el proyecto "{name}"? Se borrará el borrador guardado.',
        },
        { name: projectName }
      );
      if (!window.confirm(message)) return;
      setProjects((prev) => {
        const next = prev.filter((p) => p.id !== projectId);
        saveProjects(companyId, next);
        return next;
      });
      try {
        localStorage.removeItem(STORAGE_KEY_DRAFT(companyId || "", projectId));
      } catch {
        // ignore
      }
      if (companyId) {
        void socialAutoPostsApi.delete(companyId, projectId).catch((error) => {
          console.error("[SocialAutomatedPosts] delete project failed", error);
        });
      }
      if (currentProjectId === projectId) {
        setCurrentProjectId(null);
        setCurrentProjectName("");
      }
    },
    [companyId, intl, currentProjectId]
  );

  // Guardar borrador al cambiar config, paso o contenido generado.
  React.useEffect(() => {
    if (!currentProjectId) return;
    const t = window.setTimeout(() => {
      void persistCurrentDraft();
    }, 800);
    return () => clearTimeout(t);
  }, [
    currentProjectId,
    step,
    industry,
    topics,
    selectedDays,
    startDate,
    additionalInfo,
    contentType,
    durationWeeks,
    logo,
    images,
    marketingLineImages,
    postTextOverrides,
    postImageOverrides,
    postScheduleOverrides,
    selectedSmartTime,
    previewSeed,
    scheduledPostIds,
    persistCurrentDraft,
  ]);

  const previewDates = useMemo(() => {
    if (!startDate || selectedDays.length === 0) return [];
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const days = selectedDays.map((d) => Number(d));
    const weeks = Math.max(1, Math.min(4, durationWeeks || DEFAULT_DURATION_WEEKS));
    const totalDays = weeks * 7;
    const maxPosts = Math.min(
      MAX_POSTS_PER_MONTH,
      weeks * MAX_POSTS_PER_WEEK,
      weeks * selectedDays.length
    );
    const out: Date[] = [];
    for (let i = 0; out.length < maxPosts && i < totalDays; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      if (days.includes(date.getDay())) out.push(date);
    }
    return out;
  }, [selectedDays, startDate, durationWeeks]);

  // Mantener una temática editable por cada post planificado (opción personalizada).
  useEffect(() => {
    const n = previewDates.length;
    if (n <= 0) return;
    setCustomPostTopics((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        return [...prev, ...Array.from({ length: n - prev.length }, () => "")];
      }
      return prev.slice(0, n);
    });
  }, [previewDates.length]);

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (step === "config") {
      if (configMode === "guided") {
        if (!industry) e.industry = "required";
        if (!topics.length) e.topics = "required";
      } else if (configMode === "custom") {
        if (!customContentBrief.trim()) e.customBrief = "required";
        const filledCustom = customPostTopics.filter((t) => t.trim()).length;
        if (!filledCustom) e.customTopics = "required";
        else if (
          previewDates.length > 0 &&
          customPostTopics.slice(0, previewDates.length).some((t) => !t.trim())
        ) {
          e.customTopics = "incomplete";
        }
      } else if (configMode === "auto") {
        if (!brandDescription.trim()) e.brandDescription = "required";
      }
      if (selectedDays.length < 1) e.days = "required";
      if (selectedDays.length > 2) e.days = "max";
      if (!startDate) e.startDate = "required";
    }
    if (step === "media") {
      const filled = images.filter(Boolean).length;
      if (filled > MAX_CONTENT_IMAGES) e.images = "requiredMax";
    }
    return e;
  }, [
    brandDescription,
    configMode,
    customContentBrief,
    customPostTopics,
    images,
    industry,
    previewDates.length,
    selectedDays,
    startDate,
    step,
    topics,
  ]);

  const canProceedToMedia = useMemo(() => {
    const validDays = selectedDays.length >= 1 && selectedDays.length <= 2;
    if (!validDays || !startDate) return false;
    if (configMode === "guided") {
      return Boolean(industry && topics.length > 0);
    }
    if (configMode === "auto") {
      return Boolean(brandDescription.trim());
    }
    const planned = Math.max(previewDates.length, 1);
    const topicsOk =
      customPostTopics.length >= planned &&
      customPostTopics.slice(0, planned).every((t) => t.trim());
    return Boolean(customContentBrief.trim() && topicsOk);
  }, [
    brandDescription,
    configMode,
    customContentBrief,
    customPostTopics,
    industry,
    previewDates.length,
    selectedDays,
    startDate,
    topics.length,
  ]);

  // Con la generación por OpenAI (texto→imagen) los recursos son opcionales.
  // Basta con una configuración válida para poder crear contenido.
  const canProceedToPreview = canProceedToMedia;

  const toggleDay = useCallback(
    (dayKey: string) => {
      setSelectedDays((prev) => {
        const exists = prev.includes(dayKey);
        if (exists) return prev.filter((d) => d !== dayKey);
        if (prev.length >= 2) return prev; // max 2
        return [...prev, dayKey];
      });
    },
    [setSelectedDays]
  );

  const handleIndustryChange = useCallback(
    (value: string) => {
      setIndustry(value);
    },
    [setIndustry]
  );

  const onLogoSelected = useCallback((file: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setLogo({ file, previewUrl: url });
  }, []);

  const requestTopicIdeas = useCallback(
    async (index: number) => {
      if (!companyId) return;
      if (topicIdeasLoadingIndex != null) return;

      const seed = (customPostTopics[index] || "").trim();
      const brief = customContentBrief.trim();
      const brandCtx = brandDescription.trim();
      if (!seed && !brief && !brandCtx && !industry.trim()) {
        toast.info(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.topicIdeas.needSeed",
            defaultMessage:
              "Escribí una idea o el brief del contenido para sugerir temáticas.",
          })
        );
        return;
      }

      setTopicIdeasLoadingIndex(index);
      setTopicIdeasOpenIndex(index);
      try {
        const { data } = await socialApiClient().post(
          "openai/post-topic-ideas",
          {
            seed,
            brief,
            brandName: companyName || "",
            brandContext: brandCtx,
            industry:
              configMode === "custom"
                ? ""
                : getIndustryLabel(industry),
            count: 5,
            locale: intl.locale || "es",
          },
          {
            params: { companyId },
            timeout: 25000,
          }
        );
        const payload = data?.payload ?? data;
        const ideas = (
          Array.isArray(payload?.ideas) ? payload.ideas : []
        )
          .map((idea: unknown) => String(idea || "").trim())
          .filter(Boolean)
          .slice(0, 8);
        if (!ideas.length) {
          throw new Error("empty");
        }
        setTopicIdeasByIndex((prev) => ({ ...prev, [index]: ideas }));
      } catch (e: any) {
        setTopicIdeasOpenIndex(null);
        toast.error(
          e?.response?.data?.message ||
            e?.message ||
            intl.formatMessage({
              id: "app.socialAutomatedPosts.topicIdeas.error",
              defaultMessage:
                "No se pudieron generar ideas ahora. Probá de nuevo.",
            })
        );
      } finally {
        setTopicIdeasLoadingIndex(null);
      }
    },
    [
      brandDescription,
      companyId,
      companyName,
      configMode,
      customContentBrief,
      customPostTopics,
      industry,
      intl,
      topicIdeasLoadingIndex,
    ]
  );

  const applyTopicIdea = useCallback((index: number, idea: string) => {
    setCustomPostTopics((prev) => {
      const next = [...prev];
      next[index] = idea;
      return next;
    });
    setTopicIdeasOpenIndex(null);
  }, []);

  const addEditReferenceImages = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const incoming = Array.from(files)
        .filter((f) => f.type.startsWith("image/"))
        .slice(0, 3);
      if (!incoming.length) return;
      setEditReferenceImages((prev) => {
        const room = Math.max(0, 3 - prev.length);
        const next = incoming.slice(0, room).map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
        }));
        return [...prev, ...next];
      });
    },
    []
  );

  const removeEditReferenceImage = useCallback((index: number) => {
    setEditReferenceImages((prev) => {
      const target = prev[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const pickImagesForSlots = useCallback(
    (files: FileList | null, replaceIndex?: number | null) => {
      if (!files || files.length === 0) return;

      const fileArr = Array.from(files).slice(0, MAX_CONTENT_IMAGES);
      const urls = fileArr.map((f) => URL.createObjectURL(f));
      const nextUploads: UploadedImage[] = fileArr.map((f, idx) => ({
        file: f,
        previewUrl: urls[idx],
      }));

      setImages((prev) => {
        const out = [...prev];
        if (typeof replaceIndex === "number") {
          out[replaceIndex] = nextUploads[0] ?? null;
          return out;
        }

        // fill empty slots, then ignore extras
        let uploadIdx = 0;
        for (
          let i = 0;
          i < out.length && uploadIdx < nextUploads.length;
          i++
        ) {
          if (!out[i]) {
            out[i] = nextUploads[uploadIdx];
            uploadIdx += 1;
          }
        }
        return out;
      });
    },
    [MAX_CONTENT_IMAGES]
  );

  const clearSlot = useCallback((index: number) => {
    setImages((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  }, []);

  const pickMarketingLineImages = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const fileArr = Array.from(files).slice(0, MAX_MARKETING_LINE_IMAGES);
      const nextUploads: UploadedImage[] = fileArr.map((f) => ({
        file: f,
        previewUrl: URL.createObjectURL(f),
      }));
      setMarketingLineImages((prev) => {
        const out = [...prev];
        let uploadIdx = 0;
        for (
          let i = 0;
          i < out.length && uploadIdx < nextUploads.length;
          i++
        ) {
          if (!out[i]) {
            out[i] = nextUploads[uploadIdx];
            uploadIdx += 1;
          }
        }
        return out;
      });
    },
    [MAX_MARKETING_LINE_IMAGES]
  );

  const clearMarketingLineSlot = useCallback((index: number) => {
    setMarketingLineImages((prev) => {
      const next = [...prev];
      const current = next[index];
      if (current?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(current.previewUrl);
      }
      next[index] = null;
      return next;
    });
  }, []);

  const validateAndNext = useCallback(() => {
    if (!canProceedToMedia) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.validation.config",
          defaultMessage:
            "Completa la configuración: industria, temáticas, 1-2 días y fecha.",
        })
      );
      return;
    }
    setStep("media");
    void persistCurrentDraft();
  }, [canProceedToMedia, intl, persistCurrentDraft]);

  const previewPosts = useMemo<PreviewPost[]>(() => {
    if (step !== "preview") return [];
    const rand = mulberry32(previewSeed);
    const industryLabel =
      configMode === "custom"
        ? intl.formatMessage({
            id: "app.socialAutomatedPosts.configMode.custom.industryFallback",
            defaultMessage: "Contenido personalizado",
          })
        : configMode === "auto"
          ? intl.formatMessage({
              id: "app.socialAutomatedPosts.configMode.auto.industryFallback",
              defaultMessage: "Tu marca",
            })
          : getIndustryLabel(industry);
    const topicLabels =
      configMode === "custom"
        ? customPostTopics.map((t, i) => t.trim() || `Post ${i + 1}`)
        : configMode === "auto"
          ? buildAutoTopics(previewDates.length)
          : topics.map((t) => t.label);
    const filledImages = images
      .map((img, idx) => (img ? { idx, url: img.previewUrl } : null))
      .filter(Boolean) as Array<{ idx: number; url: string }>;

    return previewDates.map((date, idx) => {
      const id = `${idx}-${date.toISOString()}`;
      const scheduleOverride = postScheduleOverrides[id];
      const resolvedDate = scheduleOverride
        ? new Date(scheduleOverride.date)
        : new Date(date);
      const defaultTime = idx % 2 === 0 ? "09:00" : "18:00";
      const time = scheduleOverride?.time ?? selectedSmartTime ?? defaultTime;
      const topic = topicLabels.length
        ? topicLabels[idx % topicLabels.length]
        : "Tema";
      const title = `Promo ${topic} (${industryLabel})`;
      const defaultText = `Estamos generando el contenido con la IA de Wembii…`;
      const text = postTextOverrides[id] ?? defaultText;
      const imageIdx = filledImages.length
        ? filledImages[idx % filledImages.length].idx
        : 0;
      const defaultImage = filledImages.length
        ? filledImages[idx % filledImages.length]?.url ?? ""
        : "";

      const overrideImage =
        postImageOverrides[id]?.previewUrl ||
        postImageOverrides[id]?.durableUrl;
      const genStatus = jobState?.results[id]?.status;
      const generatedImage = jobState?.results[id]?.image?.previewUrl;
      const wantsDesign = contentType === "design" || contentType === "both";
      // Tras generar diseño, no mostrar la foto de referencia como si fuera la IA.
      const image =
        overrideImage ||
        generatedImage ||
        (!wantsDesign || !genStatus || genStatus === "pending"
          ? defaultImage
          : "");
      return {
        id,
        date: resolvedDate,
        time,
        title,
        text,
        image,
        confidence: Math.round((0.74 + rand() * 0.22) * 100) / 100,
        contentImageIndex: imageIdx,
      };
    });
  }, [
    configMode,
    contentType,
    customPostTopics,
    images,
    industry,
    intl,
    jobState,
    previewDates,
    previewSeed,
    step,
    topics,
    postImageOverrides,
    postScheduleOverrides,
    postTextOverrides,
    selectedSmartTime,
  ]);

  /** Si ya hay copy real o manual guardado, no lo pisa al mover fecha/hora. */
  const resolveCaptionAfterScheduleChange = useCallback(
    (postId: string, _newDate: Date, _newTime: string) => {
      const override = postTextOverrides[postId];
      if (override !== undefined && override.trim() !== "") {
        return override;
      }
      return `Estamos generando el contenido con la IA de Wembii…`;
    },
    [postTextOverrides]
  );

  // Tras generar imagen + texto en lote, el modal debe mostrar el copy real de ChatGPT.
  useEffect(() => {
    if (!isPostModalOpen || !activePreviewPost) return;
    const latest = previewPosts.find((p) => p.id === activePreviewPost.id);
    if (!latest) return;
    const draftMatchesDisplayed = postTextDraft === activePreviewPost.text;
    if (
      draftMatchesDisplayed &&
      (latest.text !== activePreviewPost.text ||
        latest.image !== activePreviewPost.image)
    ) {
      setActivePreviewPost(latest);
      setPostTextDraft(latest.text);
    }
  }, [
    previewPosts,
    isPostModalOpen,
    activePreviewPost?.id,
    activePreviewPost?.text,
    activePreviewPost?.image,
    postTextDraft,
  ]);

  const mapOpenAiPostError = useCallback(
    (error: any): string => {
      const status =
        error?.response?.status ??
        (typeof error?.request?.status === "number"
          ? error.request.status
          : undefined);
      let payload: { code?: number; message?: string } = {};
      if (error?.response?.data && typeof error.response.data === "object") {
        payload = error.response.data;
      } else if (
        error?.request?.response &&
        typeof error.request.response === "string"
      ) {
        try {
          payload = JSON.parse(error.request.response);
        } catch {
          // ignore
        }
      }
      if (status === 400) {
        const msg = String(payload.message || "").toLowerCase();
        if (msg.includes("caption edit limit") || msg.includes("ediciones de texto gratis")) {
          return intl.formatMessage({
            id: "app.socialAutomatedPosts.modal.editText.limitReached",
            defaultMessage:
              "Alcanzaste el límite de ediciones de texto gratis para este post este mes.",
          });
        }
      }
      if (status === 403) {
        const code = payload.code ?? 0;
        if (code === Errors.InsufficientQuota) {
          return intl.formatMessage({ id: "app.social.openai.quota" });
        }
        if (code === Errors.InvalidApiKey) {
          return intl.formatMessage({ id: "app.social.openai.invalidapikey" });
        }
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.forbidden",
          defaultMessage:
            "La IA de Wembii no está disponible (403). Revisa la API key de OpenAI en Integraciones o el saldo de tu cuenta.",
        });
      }
      if (status === 401) {
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.unauthorized",
          defaultMessage: "Sesión expirada. Vuelve a iniciar sesión.",
        });
      }
      if (status === 504 || status === 502 || status === 408) {
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.timeout",
          defaultMessage:
            "La IA tardó demasiado (timeout). Esperá un momento y volvé a intentar.",
        });
      }
      const msg =
        (typeof payload.message === "string" && payload.message) ||
        error?.message ||
        intl.formatMessage({ id: "app.toast.error82" });
      if (
        /504|502|408|timeout|timed out|gateway/i.test(String(msg))
      ) {
        return intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.timeout",
          defaultMessage:
            "La IA tardó demasiado (timeout). Esperá un momento y volvé a intentar.",
        });
      }
      return msg;
    },
    [intl]
  );

  const generatePostTextWithChatGpt = useCallback(
    async (
      args: {
        industryLabel: string;
        themeLabel: string;
        socialMedia: string;
        currentText?: string;
        changeRequest?: string;
        brandName?: string;
        brandContext?: string;
        postId?: string;
        isCaptionEdit?: boolean;
      },
      companyIdParam: string
    ) => {
      const {
        industryLabel,
        themeLabel,
        socialMedia,
        currentText,
        changeRequest,
        brandName,
        brandContext,
        postId,
        isCaptionEdit = false,
      } = args;
      const body = {
        industry: industryLabel,
        subject: changeRequest?.trim()
          ? `Ajuste de caption · ${themeLabel} · ${industryLabel}`
          : `${themeLabel} · ${industryLabel}`,
        emoticons: true,
        hashtags: true,
        additionalInfo: buildChatGptCaptionAdditionalInfo(
          industryLabel,
          themeLabel,
          { currentText, changeRequest, brandName, brandContext }
        ),
        socialMedias: [socialMedia],
        mode: "single",
        ...(isCaptionEdit
          ? { isCaptionEdit: true, ...(postId ? { postId } : {}) }
          : {}),
      };
      let data: any;
      try {
        const res = await socialApiClient().post("openai/posts", body, {
          params: { companyId: companyIdParam },
          timeout: 90000,
        });
        data = res.data;
      } catch (error: any) {
        throw new Error(mapOpenAiPostError(error));
      }
      const payload = data?.payload ?? data;
      const templates = Array.isArray(payload?.templates)
        ? payload.templates
        : payload?.template
          ? [payload.template]
          : Array.isArray(data?.templates)
            ? data.templates
            : [];
      const first = templates[0] as { body?: string; text?: string } | undefined;
      const content =
        first?.body ||
        first?.text ||
        (typeof payload?.body === "string" ? payload.body : "") ||
        (typeof data?.body === "string" ? data.body : "") ||
        "";
      const out = typeof content === "string" ? content.trim() : "";
      if (!out) {
        throw new Error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.openai.emptyResponse",
            defaultMessage: "La IA de Wembii no devolvió texto para el post.",
          })
        );
      }
      return out;
    },
    [intl, mapOpenAiPostError]
  );

  const captionBrandContext = useMemo(
    () =>
      buildCaptionBrandContext({
        configMode,
        industry,
        brandDescription,
        customContentBrief,
        additionalInfo,
        companyName,
        topics,
        customPostTopics,
        previewDatesLength: previewDates.length,
        customIndustryFallback: intl.formatMessage({
          id: "app.socialAutomatedPosts.configMode.custom.industryFallback",
          defaultMessage: "Contenido personalizado",
        }),
        autoIndustryFallback: intl.formatMessage({
          id: "app.socialAutomatedPosts.configMode.auto.industryFallback",
          defaultMessage: "Tu marca",
        }),
      }),
    [
      additionalInfo,
      brandDescription,
      companyName,
      configMode,
      customContentBrief,
      customPostTopics,
      industry,
      intl,
      previewDates.length,
      topics,
    ]
  );

  // Al abrir el post, si aún no hay copy real, generarlo con ChatGPT (sin depender del botón de imágenes).
  useEffect(() => {
    if (!isPostModalOpen || !activePreviewPost || !companyId) return;
    if (!hasOpenAiConfigured) return;
    if (!isPlaceholderCaption(activePreviewPost.text)) return;

    let alive = true;
    const postId = activePreviewPost.id;
    setIsGeneratingPostText(true);

    (async () => {
      try {
        const postIndex = previewPosts.findIndex((p) => p.id === postId);
        const themeLabel = captionBrandContext.themeForIndex(
          postIndex >= 0 ? postIndex : 0
        );
        const socialMedia =
          OPENAI_SOCIAL_NAME[activeSocialPreview] ?? "Instagram";
        const generatedText = await generatePostTextWithChatGpt(
          {
            industryLabel: captionBrandContext.industryLabel,
            themeLabel,
            socialMedia,
            brandName: captionBrandContext.brandName,
            brandContext: captionBrandContext.brandContext,
          },
          companyId
        );
        if (!alive) return;
        setPostTextOverrides((prev) => ({
          ...prev,
          [postId]: generatedText,
        }));
        setPostTextDraft(generatedText);
        setActivePreviewPost((prev) =>
          prev && prev.id === postId ? { ...prev, text: generatedText } : prev
        );
      } catch (e: any) {
        if (!alive) return;
        toast.error(
          e?.message ||
            intl.formatMessage({
              id: "app.socialAutomatedPosts.modal.editText.autoGenErr",
              defaultMessage:
                'No se pudo generar el texto automáticamente. Probá "Solicitar cambios".',
            })
        );
      } finally {
        if (alive) setIsGeneratingPostText(false);
      }
    })();

    return () => {
      // Si el efecto se cancela (cambio de post/deps), siempre liberar el loading.
      // Antes quedaba "Aplicando cambios…" colgado cuando el request se cancelaba
      // y el nuevo efecto no volvía a entrar (caption ya no era placeholder).
      alive = false;
      setIsGeneratingPostText(false);
    };
  }, [
    isPostModalOpen,
    activePreviewPost?.id,
    activePreviewPost?.text,
    companyId,
    previewPosts,
    captionBrandContext,
    activeSocialPreview,
    generatePostTextWithChatGpt,
    intl,
    hasOpenAiConfigured,
  ]);

  const handleEditActivePostImage = useCallback(async (promptOverride?: string) => {
    if (!activePreviewPost) return;
    if (!companyId) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
          defaultMessage: "No hay empresa disponible para editar la imagen.",
        })
      );
      return;
    }

    const prompt = (typeof promptOverride === "string" ? promptOverride : nanoEditPrompt).trim();
    if (!prompt) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errPrompt",
          defaultMessage: "Indica qué quieres mejorar en la imagen.",
        })
      );
      return;
    }

    const postId = activePreviewPost.id;
    if (!activePreviewPost.image) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoBaseImage",
          defaultMessage: "No hay una imagen base para editar en este post.",
        })
      );
      return;
    }

    const baseImageUrl = activePreviewPost.image;
    const editPrompt = prompt;
    const logoFile = includeLogo ? logo?.file ?? null : null;
    const extraRefs = editReferenceImages
      .map((img) => img.file)
      .filter(Boolean);

    setIsEditingNanoBanana(true);
    setEditingImagePostIds((prev) => ({ ...prev, [postId]: true }));
    setNanoEditProgress(10);
    try {
      // La pieza actual, el logo y referencias extra se entregan a OpenAI.
      // Así el ajuste conserva la identidad visual en vez de recrearla desde cero.
      const imageResponse = await fetch(baseImageUrl);
      const imageBlob = await imageResponse.blob();
      const baseFile = new File(
        [imageBlob],
        `post-to-edit-${Date.now()}.png`,
        { type: imageBlob.type || "image/png" }
      );
      const formData = new FormData();
      formData.append(
        "prompt",
        [
          "Edita la pieza gráfica adjunta para una publicación de redes sociales.",
          `Cambio solicitado: ${editPrompt}`,
          "La PRIMERA imagen es la pieza a editar. Las siguientes son referencias (logo, producto, estilo o moodboard).",
          "Conserva el mensaje y la composición útil de la pieza base.",
          "Si hay logo o imágenes de referencia, intégralos de forma clara, proporcionada y legible cuando el pedido lo requiera.",
          "Reduce texto si hay exceso: deja como máximo un titular corto y una línea de apoyo, sin frases redundantes.",
          "Entrega una única pieza final profesional a sangre (full-bleed).",
          "PROHIBIDO: marcos, bordes decorativos, filetes, passepartout o bordes de color (azul, violeta, negro, blanco) alrededor del diseño.",
          "No agregues ni dejes nombres de mes, años de calendario ni fechas de publicación del plan.",
          "Sin marcos de dispositivo ni interfaz de red social.",
        ].join("\n")
      );
      formData.append("size", openAiSizeForAspect(imageAspect));
      formData.append("images", baseFile);
      if (logoFile) formData.append("images", logoFile);
      extraRefs.forEach((file, idx) => {
        formData.append(
          "images",
          file,
          file.name || `edit-reference-${idx + 1}.png`
        );
      });

      const { data } = await socialApiClient().post(
        "openai/images",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { companyId },
          timeout: 240000,
        }
      );

      const payload = data?.payload ?? data;
      const mimeType = payload?.mimeType || "image/png";
      const responseUrl =
        typeof payload?.url === "string" ? payload.url.trim() : "";
      const durableUrl = isDurableHttpUrl(responseUrl)
        ? responseUrl
        : undefined;
      let blob: Blob;
      if (payload?.b64_json) {
        const binary = atob(payload.b64_json);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: mimeType });
      } else if (responseUrl) {
        const generatedResponse = await fetch(responseUrl);
        blob = await generatedResponse.blob();
      } else {
        throw new Error("OpenAI no devolvió una imagen editada.");
      }

      setNanoEditProgress(100);
      const contentTypeImg = blob.type || mimeType;
      const ext = contentTypeImg.includes("png")
        ? "png"
        : contentTypeImg.includes("webp")
          ? "webp"
          : "jpg";

      const file = new File(
        [blob],
        `openai-edit-${Date.now()}.${ext}`,
        { type: contentTypeImg }
      );
      const previewUrl = durableUrl || URL.createObjectURL(file);

      setPostImageOverrides((prev) => {
        const prevItem = prev[postId];
        if (prevItem?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(prevItem.previewUrl);
        }
        return {
          ...prev,
          [postId]: { file, previewUrl, durableUrl },
        };
      });

      setActivePreviewPost((prev) => {
        if (!prev || prev.id !== postId) return prev;
        return { ...prev, image: previewUrl };
      });

      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.editImage.editedOk",
          defaultMessage: "Imagen actualizada correctamente",
        })
      );
      setNanoEditPrompt("");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(WEMBII_AI_CREDITS_REFRESH_EVENT));
      }
    } catch (e: any) {
      const apiMsg = String(
        e?.response?.data?.message || e?.message || ""
      ).toLowerCase();
      const insufficient = apiMsg.includes("insufficient ai credits");
      toast.error(
        insufficient
          ? intl.formatMessage({
              id: "app.socialAutomatedPosts.credits.insufficient",
              defaultMessage:
                "No hay créditos de IA suficientes para editar la imagen.",
            })
          : e?.response?.data?.message ||
              e?.message ||
              intl.formatMessage({
                id: "app.socialAutomatedPosts.nanobanana.editedErr",
                defaultMessage: "Ocurrió un error editando la imagen.",
              })
      );
    } finally {
      setIsEditingNanoBanana(false);
      setEditingImagePostIds((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      setNanoEditProgress(0);
    }
  }, [
    activePreviewPost,
    companyId,
    editReferenceImages,
    imageAspect,
    includeLogo,
    intl,
    logo,
    nanoEditPrompt,
  ]);

  const handleChangePostImageByFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!activePreviewPost) return;
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;
      const previewUrl = URL.createObjectURL(file);
      setPostImageOverrides((prev) => {
        const prevItem = prev[activePreviewPost.id];
        if (prevItem?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(prevItem.previewUrl);
        }
        return {
          ...prev,
          [activePreviewPost.id]: { file, previewUrl },
        };
      });
      setActivePreviewPost((prev) =>
        prev ? { ...prev, image: previewUrl } : prev
      );
      e.target.value = "";
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.changeImage.uploaded",
          defaultMessage: "Imagen reemplazada correctamente",
        })
      );
    },
    [activePreviewPost, intl]
  );

  const monthBase = useMemo(() => {
    const base = previewDates?.[0]
      ? new Date(previewDates[0])
      : startDate
        ? new Date(startDate)
        : new Date(2026, 3, 1);
    return new Date(
      base.getFullYear(),
      base.getMonth() + calendarMonthOffset,
      1
    );
  }, [calendarMonthOffset, previewDates, startDate]);

  const calendarModel = useMemo(() => {
    const monthStart = new Date(monthBase.getFullYear(), monthBase.getMonth(), 1);
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const totalDays = monthEnd.getDate();
    const firstDayIndex = monthStart.getDay(); // 0..6 (Dom..Sáb)

    const daysByKey = new Map<string, typeof previewPosts[number]>();
    previewPosts.forEach((p) => {
      const key = formatDate(p.date);
      daysByKey.set(key, p);
    });

    const locale = intl.locale || "es";
    const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(
      monthStart
    );
    const title = `${monthName} ${monthStart.getFullYear()}`;

    const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const cells: Array<{ key: string | null; dayNumber: number | null; date: Date | null }> =
      [];

    for (let i = 0; i < firstDayIndex; i++) {
      cells.push({ key: null, dayNumber: null, date: null });
    }
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(monthStart);
      date.setDate(day);
      const key = formatDate(date);
      cells.push({
        key,
        dayNumber: day,
        date,
      });
    }

    return { title, weekDays, cells, daysByKey };
  }, [intl.locale, monthBase, previewPosts]);

  // Rehidrata el estado del job de generación al abrir/volver al proyecto.
  useEffect(() => {
    if (!jobKey) {
      setJobState(null);
      return;
    }
    setJobState(autoPostGenerationManager.getState(jobKey));
    const unsubscribe = autoPostGenerationManager.subscribe(jobKey, (state) => {
      setJobState(state);
    });
    return unsubscribe;
  }, [jobKey]);

  // Cronómetro del loader mientras se genera contenido.
  useEffect(() => {
    if (!isGeneratingContent) return;
    const timer = window.setInterval(() => setGenNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isGeneratingContent]);

  // Vuelca los resultados (imagen/texto) del job a los overrides del calendario.
  useEffect(() => {
    if (!jobState) return;
    const results = jobState.results;
    setPostImageOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(results).forEach(([postId, r]) => {
        if (r.image && next[postId]?.previewUrl !== r.image.previewUrl) {
          next[postId] = {
            file: r.image.file,
            previewUrl: r.image.previewUrl,
            durableUrl: r.image.durableUrl,
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setPostTextOverrides((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(results).forEach(([postId, r]) => {
        if (r.text && next[postId] !== r.text) {
          next[postId] = r.text;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [jobState]);

  const buildGenerationTasks = useCallback((): AutoPostGenTask[] => {
    const topicLabels =
      configMode === "custom"
        ? customPostTopics.map((t, i) => t.trim() || `Post ${i + 1}`)
        : configMode === "auto"
          ? buildAutoTopics(previewDates.length)
          : topics.map((t) => t.label);
    const filledImages = images
      .map((img) => (img ? img.file : null))
      .filter(Boolean) as File[];

    return previewDates.map((date, idx) => {
      const id = `${idx}-${date.toISOString()}`;
      const topicLabel = topicLabels.length
        ? topicLabels[idx % topicLabels.length]
        : "General";
      const referenceFile = filledImages.length
        ? filledImages[idx % filledImages.length]
        : null;
      return { id, topicLabel, socialMedia: "Instagram", referenceFile };
    });
  }, [configMode, customPostTopics, images, previewDates, topics]);

  const startGeneration = useCallback(
    (goToPreview: boolean) => {
      if (!canProceedToPreview) {
        toast.error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.validation.config",
            defaultMessage:
              "Completa la configuración antes de generar el contenido.",
          })
        );
        return;
      }
      if (!companyId || !jobKey) {
        toast.error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
            defaultMessage: "No hay empresa disponible para generar contenido.",
          })
        );
        return;
      }
      const tasks = buildGenerationTasks();
      if (!tasks.length) return;

      const wantsText = contentType === "text" || contentType === "both";
      if (wantsText && !hasOpenAiConfigured) {
        toast.warning(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.openai.notConfigured",
            defaultMessage:
              "La IA de Wembii no está disponible en este momento. Reintenta más tarde o contacta a soporte.",
          })
        );
      }

      const industryLabel = captionBrandContext.industryLabel;

      let generationExtraInfo = [
        captionBrandContext.brandContext,
        configMode === "auto"
          ? "Varía el enfoque de cada post (presentación, tip, prueba social, CTA, etc.) manteniendo coherencia de marca."
          : "",
      ]
        .filter(Boolean)
        .join("\n");

      // Limpia overrides previos para que la regeneración muestre contenido fresco.
      setPostImageOverrides((prev) => {
        Object.values(prev).forEach((item) => {
          if (item?.previewUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(item.previewUrl);
          }
        });
        return {};
      });
      setPostTextOverrides({});
      setApprovalMode(false);
      setApprovedPostIds({});
      setScheduledPostIds({});
      setPreviewSeed(Date.now());

      autoPostGenerationManager.start({
        jobKey,
        companyId,
        industryLabel,
        additionalInfo: generationExtraInfo,
        marketingLineFiles: marketingLineImages
          .filter((img): img is UploadedImage => Boolean(img?.file?.size))
          .map((img) => img.file),
        brandName: captionBrandContext.brandName || companyName || "",
        contentType,
        hasOpenAi: hasOpenAiConfigured,
        logoFile:
          includeLogo && logo?.file && logo.file.size > 0 ? logo.file : null,
        projectName: currentProjectName,
        posts: tasks,
        imageAspect,
        imageTextDensity,
      });

      if (goToPreview) {
        setStep("preview");
        persistCurrentDraft();
      }
    },
    [
      buildGenerationTasks,
      canProceedToPreview,
      captionBrandContext,
      companyId,
      companyName,
      configMode,
      contentType,
      currentProjectName,
      hasOpenAiConfigured,
      includeLogo,
      intl,
      jobKey,
      logo,
      marketingLineImages,
      imageAspect,
      imageTextDensity,
      persistCurrentDraft,
    ]
  );

  const stopGeneration = useCallback(() => {
    if (jobKey) autoPostGenerationManager.stop(jobKey);
    toast.info(
      intl.formatMessage({
        id: "app.socialAutomatedPosts.generate.stopped",
        defaultMessage:
          "Generación detenida. Los posts ya creados se conservan.",
      })
    );
  }, [jobKey, intl]);

  const applyPostTextDraft = useCallback(() => {
    if (!activePreviewPost) return;
    const value = postTextDraft.trim();
    if (!value) return;
    setPostTextOverrides((prev) => ({
      ...prev,
      [activePreviewPost.id]: value,
    }));
    setActivePreviewPost((prev) => (prev ? { ...prev, text: value } : prev));
    toast.success(
      intl.formatMessage({
        id: "app.socialAutomatedPosts.modal.editText.applied",
        defaultMessage: "Texto actualizado correctamente",
      })
    );
  }, [activePreviewPost, intl, postTextDraft]);

  const requestPostTextChanges = useCallback(async () => {
    if (!activePreviewPost || !companyId) return;
    const changeRequest = textChangePrompt.trim();
    if (!changeRequest) {
      toast.warning(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.editText.changeRequired",
          defaultMessage: "Escribí qué cambios querés que haga la IA.",
        })
      );
      return;
    }
    if (!hasOpenAiConfigured) {
      toast.warning(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.notConfigured",
          defaultMessage:
            "La IA de Wembii no está disponible en este momento. Reintenta más tarde o contacta a soporte.",
        })
      );
      return;
    }
    setIsGeneratingPostText(true);
    try {
      const postIndex = previewPosts.findIndex(
        (p) => p.id === activePreviewPost.id
      );
      const themeLabel = captionBrandContext.themeForIndex(
        postIndex >= 0 ? postIndex : 0
      );
      const socialMedia =
        OPENAI_SOCIAL_NAME[activeSocialPreview] ?? "Instagram";
      const currentText =
        postTextDraft.trim() || activePreviewPost.text || "";
      const generatedText = await generatePostTextWithChatGpt(
        {
          industryLabel: captionBrandContext.industryLabel,
          themeLabel,
          socialMedia,
          currentText,
          changeRequest,
          brandName: captionBrandContext.brandName,
          brandContext: captionBrandContext.brandContext,
          postId: activePreviewPost.id,
          isCaptionEdit: true,
        },
        companyId
      );
      setPostTextOverrides((prev) => ({
        ...prev,
        [activePreviewPost.id]: generatedText,
      }));
      setPostTextDraft(generatedText);
      setActivePreviewPost((prev) =>
        prev ? { ...prev, text: generatedText } : prev
      );
      setTextChangePrompt("");
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.editText.changesApplied",
          defaultMessage: "Texto actualizado con tus cambios",
        })
      );
    } catch (e: any) {
      toast.error(
        e?.message ||
          intl.formatMessage({
            id: "app.socialAutomatedPosts.modal.editText.regenerateErr",
            defaultMessage: "No se pudo regenerar el texto del post.",
          })
      );
    } finally {
      setIsGeneratingPostText(false);
    }
  }, [
    activePreviewPost,
    activeSocialPreview,
    captionBrandContext,
    companyId,
    generatePostTextWithChatGpt,
    intl,
    previewPosts,
    hasOpenAiConfigured,
    textChangePrompt,
    postTextDraft,
  ]);

  const requestAlternativePostText = useCallback(async () => {
    if (!activePreviewPost || !companyId) return;
    if (!hasOpenAiConfigured) {
      toast.warning(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.openai.notConfigured",
          defaultMessage:
            "La IA de Wembii no está disponible en este momento. Reintenta más tarde o contacta a soporte.",
        })
      );
      return;
    }
    if (isPlaceholderCaption(activePreviewPost.text)) {
      toast.info(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.editText.alternativeWait",
          defaultMessage:
            "Esperá a que termine de generarse el texto actual para pedir otra opción.",
        })
      );
      return;
    }

    setIsGeneratingPostText(true);
    try {
      const postIndex = previewPosts.findIndex(
        (p) => p.id === activePreviewPost.id
      );
      const themeLabel = captionBrandContext.themeForIndex(
        postIndex >= 0 ? postIndex : 0
      );
      const socialMedia =
        OPENAI_SOCIAL_NAME[activeSocialPreview] ?? "Instagram";
      const currentText =
        postTextDraft.trim() || activePreviewPost.text || "";
      const generatedText = await generatePostTextWithChatGpt(
        {
          industryLabel: captionBrandContext.industryLabel,
          themeLabel,
          socialMedia,
          currentText,
          changeRequest:
            "Generá OTRA opción de caption distinta a la actual: nuevo gancho, otra forma de explicar el beneficio y un CTA diferente. Mantené la misma marca, rubro e idioma. No copies frases literales del texto actual.",
          brandName: captionBrandContext.brandName,
          brandContext: captionBrandContext.brandContext,
          postId: activePreviewPost.id,
          isCaptionEdit: true,
        },
        companyId
      );
      setPostTextOverrides((prev) => ({
        ...prev,
        [activePreviewPost.id]: generatedText,
      }));
      setPostTextDraft(generatedText);
      setActivePreviewPost((prev) =>
        prev ? { ...prev, text: generatedText } : prev
      );
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.modal.editText.alternativeApplied",
          defaultMessage: "Nueva opción de texto lista",
        })
      );
    } catch (e: any) {
      toast.error(
        e?.message ||
          intl.formatMessage({
            id: "app.socialAutomatedPosts.modal.editText.regenerateErr",
            defaultMessage: "No se pudo regenerar el texto del post.",
          })
      );
    } finally {
      setIsGeneratingPostText(false);
    }
  }, [
    activePreviewPost,
    activeSocialPreview,
    captionBrandContext,
    companyId,
    generatePostTextWithChatGpt,
    hasOpenAiConfigured,
    intl,
    postTextDraft,
    previewPosts,
  ]);

  const handleCreate = useCallback(async () => {
    if (!canProceedToPreview || !previewDates.length) return;

    if (!companyId) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.nanobanana.errNoCompany",
          defaultMessage:
            "No hay companyId disponible para NanoBanana.",
        })
      );
      return;
    }

    const selectedPosts = previewPosts.filter((p) =>
      Boolean(approvedPostIds[p.id])
    );

    if (!selectedPosts.length) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.schedule.selectRequired",
          defaultMessage:
            "Seleccioná al menos una publicación para programar en las redes sociales.",
        })
      );
      return;
    }

    const syncedTargets = getAutoPostRequiredSocials().filter(
      (s) =>
        Boolean(selectedScheduleNetworks[s.previewId]) &&
        (scheduleEligibleNetworks.includes(s.previewId) ||
          isAutoPostSocialSynced(
            socialSettings as Record<string, unknown>,
            s.previewId
          ))
    );
    if (!syncedTargets.length) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.schedule.networksRequired",
          defaultMessage:
            "Selecciona al menos una red social sincronizada para programar.",
        })
      );
      return;
    }

    setIsGenerating(true);
    try {
      const filledUploads = images.filter(Boolean) as UploadedImage[];
      const imageByFileKey = new Map<string, Promise<PostImage>>();
      let sentCount = 0;
      let failedCount = 0;
      const newlyScheduled: Record<string, boolean> = {};

      const resolvePostImage = async (
        upload: UploadedImage | undefined,
        idx: number
      ): Promise<PostImage | null> => {
        if (!upload) return null;
        if (upload.file && upload.file.size > 0) {
          const file = upload.file;
          const key = `${file.name}_${file.lastModified}_${file.size}`;
          if (!imageByFileKey.has(key)) {
            imageByFileKey.set(
              key,
              (async () => {
                const data = (await getBase64(file)) as string;
                return {
                  id: `${key}_${idx}`,
                  lastModified: file.lastModified,
                  name: file.name,
                  size: file.size,
                  type: file.type || "image/jpeg",
                  data,
                };
              })()
            );
          }
          return imageByFileKey.get(key)!;
        }
        const dataUrl =
          (await previewUrlToDataUrl(upload.previewUrl)) || upload.previewUrl;
        if (!dataUrl?.startsWith("data:")) return null;
        const mimeMatch = /^data:([^;]+);/.exec(dataUrl);
        const mime = mimeMatch?.[1] || "image/jpeg";
        return {
          id: `preview_${idx}_${Date.now()}`,
          lastModified: Date.now(),
          name: `post-${idx}.jpg`,
          size: dataUrl.length,
          type: mime,
          data: dataUrl,
        };
      };

      for (let idx = 0; idx < selectedPosts.length; idx += 1) {
        const post = selectedPosts[idx];
        const upload =
          postImageOverrides[post.id] ||
          images[post.contentImageIndex] ||
          (filledUploads.length
            ? filledUploads[idx % filledUploads.length]
            : undefined);

        const text = (postTextOverrides[post.id] ?? post.text ?? "").trim();
        const scheduleDate = buildScheduleDate(post.date, post.time);
        let postImage = await resolvePostImage(upload, idx);
        if (!postImage && post.image) {
          postImage = await resolvePostImage(
            { file: new File([], `post-${idx}.jpg`), previewUrl: post.image },
            idx
          );
        }

        if (!postImage && contentType !== "text") {
          failedCount += syncedTargets.length;
          continue;
        }

        let postSucceeded = false;
        for (const target of syncedTargets) {
          try {
            const imagesPayload = postImage ? [postImage] : [];
            if (target.previewId === "instagram") {
              const viaLogin = shouldPostInstagramViaLogin(
                socialSettings as Record<string, unknown>
              );
              if (viaLogin) {
                await instagramLoginApiService.socialPost(
                  text,
                  [],
                  imagesPayload,
                  scheduleDate,
                  "",
                  companyId
                );
              } else {
                await instagramApiService.socialPost(
                  text,
                  [],
                  imagesPayload,
                  scheduleDate,
                  "",
                  companyId
                );
              }
            } else if (target.previewId === "facebook") {
              await facebookApiService.socialPost(
                text,
                [],
                imagesPayload,
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "linkedin") {
              await linkedInApiService.socialPost(
                text,
                [],
                imagesPayload,
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "twitter") {
              await twitterApiService.socialPost(
                text,
                [],
                imagesPayload,
                scheduleDate,
                "",
                companyId
              );
            } else if (target.previewId === "tiktok") {
              await tiktokApiService.socialPost(
                text,
                [],
                imagesPayload,
                scheduleDate,
                "",
                companyId
              );
            }
            sentCount += 1;
            postSucceeded = true;
          } catch (error) {
            failedCount += 1;
            // eslint-disable-next-line no-console
            console.error("Error sending automated post to calendar", {
              error,
              target: target.previewId,
              postId: post.id,
            });
          }
        }
        if (postSucceeded) {
          newlyScheduled[post.id] = true;
        }
      }

      if (Object.keys(newlyScheduled).length > 0) {
        setScheduledPostIds((prev) => ({ ...prev, ...newlyScheduled }));
      }

      if (sentCount > 0 && failedCount === 0) {
        toast.success(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.sendCalendar.ok",
            defaultMessage:
              "Publicaciones programadas en las redes sociales correctamente.",
          })
        );
      } else if (sentCount > 0) {
        toast.warn(
          intl.formatMessage(
            {
              id: "app.socialAutomatedPosts.sendCalendar.partial",
              defaultMessage:
                "Se programaron {ok} publicaciones, pero {fail} fallaron. Revisá las conexiones e intentá de nuevo.",
            },
            { ok: sentCount, fail: failedCount }
          )
        );
      } else {
        toast.error(
          intl.formatMessage({
            id: "app.socialAutomatedPosts.sendCalendar.error",
            defaultMessage:
              "No se pudo programar en las redes. Revisá las conexiones e intentá de nuevo.",
          })
        );
      }

      setApprovalMode(false);
      setApprovedPostIds({});
      setScheduleNetworksModalOpen(false);
      if (sentCount > 0) {
        setScheduleSuccessStats({
          sent: sentCount,
          failed: failedCount,
          networks: syncedTargets.map((t) => t.previewId),
        });
        setScheduleSuccessModalOpen(true);
      }
    } finally {
      setIsGenerating(false);
    }
  }, [
    approvedPostIds,
    canProceedToPreview,
    companyId,
    contentType,
    images,
    previewDates,
    previewPosts,
    intl,
    missingRequiredSocials,
    postImageOverrides,
    postTextOverrides,
    selectedScheduleNetworks,
    scheduleEligibleNetworks,
    socialSettings,
  ]);

  const selectedCount = useMemo(() => {
    return previewPosts.reduce(
      (acc, p) => acc + (approvedPostIds[p.id] ? 1 : 0),
      0
    );
  }, [approvedPostIds, previewPosts]);

  const canSendToCalendar = hasAnySocialSynced && selectedCount > 0;

  const handleApplyEditedImageBlob = useCallback(
    async (blob: Blob) => {
      if (!activePreviewPost) return;
      const file = new File(
        [blob],
        `post-edit-${Date.now()}.jpg`,
        { type: blob.type || "image/jpeg" }
      );
      const previewUrl = URL.createObjectURL(file);
      setPostImageOverrides((prev) => {
        const prevItem = prev[activePreviewPost.id];
        if (prevItem?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(prevItem.previewUrl);
        }
        return {
          ...prev,
          [activePreviewPost.id]: { file, previewUrl },
        };
      });
      setActivePreviewPost((prev) =>
        prev ? { ...prev, image: previewUrl } : prev
      );
      setImageEditorOpen(false);
      toast.success(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.imageEditor.applied",
          defaultMessage: "Imagen actualizada",
        })
      );
    },
    [activePreviewPost, intl]
  );

  const openScheduleNetworksModal = useCallback(async () => {
    if (selectedCount <= 0) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.schedule.selectRequired",
          defaultMessage:
            "Selecciona al menos una publicación para programar en las redes sociales.",
        })
      );
      return;
    }

    let settings = (socialSettings || null) as Record<string, unknown> | null;
    if (companyId) {
      try {
        // Lectura fresca desde API (evita race con Redux stale / Instagram Login).
        const fresh = await meApiService.getMeSocialSetting(companyId);
        settings = (fresh || null) as Record<string, unknown> | null;
        // Mantener el store al día para el resto de la UI.
        void getMeSocialSettings(companyId);
      } catch {
        // Si falla el refresh, usamos lo que haya en store.
      }
    }

    const synced = getAutoPostRequiredSocials().filter((s) =>
      isAutoPostSocialSynced(settings, s.previewId)
    );

    if (!synced.length) {
      toast.error(
        intl.formatMessage({
          id: "app.socialAutomatedPosts.syncMissing",
          defaultMessage:
            "Para programar, primero sincroniza las redes sociales requeridas.",
        })
      );
      setSyncAccountsModalOpen(true);
      return;
    }

    setSelectedScheduleNetworks(
      Object.fromEntries(synced.map((s) => [s.previewId, true]))
    );
    setScheduleEligibleNetworks(synced.map((s) => s.previewId));
    setScheduleNetworksModalOpen(true);
  }, [
    companyId,
    getMeSocialSettings,
    intl,
    selectedCount,
    socialSettings,
  ]);

  const allSelected = useMemo(() => {
    return (
      previewPosts.length > 0 &&
      previewPosts.every((p) => Boolean(approvedPostIds[p.id]))
    );
  }, [approvedPostIds, previewPosts]);

  // Estadísticas del loader de generación (tiempo transcurrido + estimado restante).
  const generationStats = useMemo(() => {
    if (!jobState) {
      return {
        elapsedSec: 0,
        etaSec: 0,
        avgSecPerPost: 0,
        done: 0,
        total: 0,
      };
    }
    const endRef = jobState.endedAt ?? genNow;
    const elapsedMs = Math.max(0, endRef - jobState.startedAt);
    const elapsedSec = Math.floor(elapsedMs / 1000);
    const done = jobState.done;
    const total = jobState.total;
    const remaining = Math.max(0, total - done);
    // Estimación base hasta tener al menos 1 post terminado.
    const baselineMs =
      contentType === "text"
        ? 14000
        : contentType === "design"
          ? 38000
          : 48000;
    const avgPerPostMs = done > 0 ? elapsedMs / done : baselineMs;
    const avgSecPerPost = Math.max(1, Math.ceil(avgPerPostMs / 1000));
    // Tiempo ya consumido dentro del lote restante → cuenta regresiva real.
    const elapsedIntoRemaining = Math.max(
      0,
      elapsedSec - done * avgSecPerPost
    );
    const rawEta = remaining * avgSecPerPost - elapsedIntoRemaining;
    const etaSec = remaining > 0 ? Math.max(1, Math.ceil(rawEta)) : 0;
    return {
      elapsedSec,
      etaSec,
      avgSecPerPost,
      done,
      total,
    };
  }, [jobState, genNow, contentType]);

  /** ETA en cuenta regresiva (segundos) para un post concreto del job. */
  const etaSecForPost = useCallback(
    (postId: string): number | null => {
      if (!jobState || jobState.status !== "running") return null;
      const order = previewPosts.map((p) => p.id);
      const index = order.indexOf(postId);
      if (index < 0) return generationStats.etaSec || null;
      const result = jobState.results[postId];
      if (result?.status === "done" || result?.status === "error") return null;
      const avg = generationStats.avgSecPerPost || 1;
      const queuePosition = Math.max(0, index - jobState.done);
      const postsLeftForThis = queuePosition + 1;
      const elapsedIntoRemaining = Math.max(
        0,
        generationStats.elapsedSec - jobState.done * avg
      );
      return Math.max(
        1,
        Math.ceil(postsLeftForThis * avg - elapsedIntoRemaining)
      );
    },
    [
      generationStats.avgSecPerPost,
      generationStats.elapsedSec,
      generationStats.etaSec,
      jobState,
      previewPosts,
    ]
  );

  const isPostStillGenerating = useCallback(
    (postId: string) => {
      if (!isGeneratingContent || !jobState) return false;
      const status = jobState.results[postId]?.status;
      return !status || status === "pending";
    },
    [isGeneratingContent, jobState]
  );

  // Solo plan Premium.
  if (companyPlan && !isPremiumPlan) {
    return (
      <Layout>
        <Header2
          title={intl.formatMessage({
            id: "app.sidebar.socialMediaAutomatedPosts",
          })}
        />
        <Content>
          <div className="social-auto social-auto--planLocked">
            <div className="social-auto__previewIntroTitle">
              {intl.formatMessage({ id: "app.plan.upgrade" })}
            </div>
            <p className="text-muted mb-3">
              {intl.formatMessage({
                id: "app.sidebar.disabled.planNotAllowed",
              })}
            </p>
            <UpgradePlanButton />
          </div>
        </Content>
      </Layout>
    );
  }

  // Vista listado de proyectos (antes de empezar el flujo).
  if (!currentProjectId) {
    return (
      <Layout>
        <Header2
          title={intl.formatMessage({
            id: "app.sidebar.socialMediaAutomatedPosts",
          })}
        />
        <Content className="p-0">
          <div className="social-auto">
            <div className="social-auto__top">
              <div className="social-auto__previewIntroTitle">
                {intl.formatMessage({
                  id: "app.socialAutomatedPosts.projects.title",
                  defaultMessage: "Proyectos de publicaciones IA",
                })}
              </div>
              <div className="social-auto__subtitle">
                {intl.formatMessage({
                  id: "app.socialAutomatedPosts.projects.subtitle",
                  defaultMessage:
                    "Cada proyecto es una serie de publicaciones. Crea uno para empezar o abre uno existente para seguir editando.",
                })}
              </div>
            </div>
            <div className="social-auto__body">
              <div className="social-auto__panel social-auto__panel--right">
                <div className="social-auto__projectCreate">
                  <Input
                    type="text"
                    placeholder={intl.formatMessage({
                      id: "app.socialAutomatedPosts.projects.namePlaceholder",
                      defaultMessage: "Nombre del proyecto",
                    })}
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        createProject(newProjectName);
                        setNewProjectName("");
                      }
                    }}
                    className="social-auto__projectNameInput"
                  />
                  <Button
                    color="primary"
                    onClick={() => {
                      createProject(newProjectName);
                      setNewProjectName("");
                    }}
                  >
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.projects.create",
                      defaultMessage: "Crear proyecto",
                    })}
                  </Button>
                </div>
                <div className="social-auto__projectList">
                  {projects.length === 0 ? (
                    <div className="social-auto__emptyIntro">
                      <div className="social-auto__emptyIntroLead">
                        <div className="social-auto__emptyIntroEyebrow">
                          <FontAwesomeIcon icon={["fas", "wand-magic-sparkles"]} />
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.empty.eyebrow",
                            defaultMessage: "Plan Premium",
                          })}
                        </div>
                        <h3 className="social-auto__emptyIntroTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.empty.title",
                            defaultMessage:
                              "Planifica semanas de contenido en minutos",
                          })}
                        </h3>
                        <p className="social-auto__emptyIntroText">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.empty.lead",
                            defaultMessage:
                              "Publicaciones IA crea series completas de posts con imagen y texto listos para tus redes. Configuras una vez, la IA genera el resto y tú solo revisas, ajustas y programas. Ideal para ahorrar horas de diseño y redacción cada semana.",
                          })}
                        </p>
                      </div>

                      <div className="social-auto__emptyIntroBenefits">
                        <div className="social-auto__emptyIntroBenefit">
                          <FontAwesomeIcon icon={["fas", "clock"]} />
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.benefit1",
                              defaultMessage:
                                "Ahorro de tiempo: de horas de trabajo a unos minutos de revisión",
                            })}
                          </span>
                        </div>
                        <div className="social-auto__emptyIntroBenefit">
                          <FontAwesomeIcon icon={["fas", "image"]} />
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.benefit2",
                              defaultMessage:
                                "Imagen + caption con emojis y hashtags, listos para publicar",
                            })}
                          </span>
                        </div>
                        <div className="social-auto__emptyIntroBenefit">
                          <FontAwesomeIcon icon={["fas", "sliders-h"]} />
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.benefit3",
                              defaultMessage:
                                "Control total: edita texto o imagen con IA antes de programar",
                            })}
                          </span>
                        </div>
                      </div>

                      <div className="social-auto__emptyIntroHow">
                        <div className="social-auto__emptyIntroHowTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.empty.howTitle",
                            defaultMessage: "Cómo funciona",
                          })}
                        </div>
                        <ol className="social-auto__emptyIntroSteps">
                          <li>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.step1",
                              defaultMessage:
                                "Crea un proyecto y define industria, temáticas y días de publicación.",
                            })}
                          </li>
                          <li>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.step2",
                              defaultMessage:
                                "Sube tu logo e imágenes de marca para que la IA respete tu identidad.",
                            })}
                          </li>
                          <li>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.empty.step3",
                              defaultMessage:
                                "Revisa el calendario generado, ajusta lo que quieras y programa en tus redes conectadas.",
                            })}
                          </li>
                        </ol>
                      </div>

                      <p className="social-auto__emptyIntroHint">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.projects.empty",
                          defaultMessage:
                            "Aún no hay proyectos. Escribe un nombre arriba y crea el primero para empezar.",
                        })}
                      </p>

                      <div
                        className="social-auto__emptyVisual"
                        aria-hidden="true"
                      >
                        <div className="social-auto__emptyVisualGlow" />
                        <div className="social-auto__emptyVisualStage">
                          <div className="social-auto__emptyVisualPost social-auto__emptyVisualPost--main">
                            <div className="social-auto__emptyVisualPostHead">
                              <span className="social-auto__emptyVisualAvatar" />
                              <span className="social-auto__emptyVisualMeta">
                                <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--sm" />
                                <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--xs" />
                              </span>
                            </div>
                            <div className="social-auto__emptyVisualMedia">
                              <div className="social-auto__emptyVisualShimmer" />
                              <div className="social-auto__emptyVisualSpark">
                                <FontAwesomeIcon
                                  icon={["fas", "wand-magic-sparkles"]}
                                />
                              </div>
                            </div>
                            <div className="social-auto__emptyVisualCaption">
                              <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--a" />
                              <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--b" />
                              <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--c" />
                            </div>
                          </div>
                          <div className="social-auto__emptyVisualPost social-auto__emptyVisualPost--side social-auto__emptyVisualPost--left">
                            <div className="social-auto__emptyVisualMedia social-auto__emptyVisualMedia--mini">
                              <div className="social-auto__emptyVisualShimmer" />
                            </div>
                            <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--xs" />
                          </div>
                          <div className="social-auto__emptyVisualPost social-auto__emptyVisualPost--side social-auto__emptyVisualPost--right">
                            <div className="social-auto__emptyVisualMedia social-auto__emptyVisualMedia--mini">
                              <div className="social-auto__emptyVisualShimmer" />
                            </div>
                            <span className="social-auto__emptyVisualLine social-auto__emptyVisualLine--xs" />
                          </div>
                          <div className="social-auto__emptyVisualOrbit social-auto__emptyVisualOrbit--1" />
                          <div className="social-auto__emptyVisualOrbit social-auto__emptyVisualOrbit--2" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    projects.map((p) => (
                      <div
                        key={p.id}
                        className={cn("social-auto__projectCard", {
                          "social-auto__projectCard--hasContent":
                            p.hasGeneratedContent,
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={() => openProject(p)}
                      >
                        <div className="social-auto__projectCardMain">
                          <div className="social-auto__projectCardName">{p.name}</div>
                          <div className="social-auto__projectCardMeta text-muted small">
                            <span>
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.projects.createdOn",
                                defaultMessage: "Creado el",
                              })}{" "}
                              {new Date(p.createdAt).toLocaleString(
                                intl.locale || "es",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </span>
                            {p.hasGeneratedContent ? (
                              <span className="social-auto__projectCardBadge">
                                {intl.formatMessage({
                                  id: "app.socialAutomatedPosts.projects.hasContent",
                                  defaultMessage: "Con contenido generado",
                                })}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openProject(p);
                          }}
                        >
                          {p.hasGeneratedContent
                            ? intl.formatMessage({
                                id: "app.socialAutomatedPosts.projects.viewContent",
                                defaultMessage: "Ver contenido",
                              })
                            : intl.formatMessage({
                                id: "app.socialAutomatedPosts.projects.open",
                                defaultMessage: "Abrir",
                              })}
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProject(p.id, p.name);
                          }}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.projects.delete",
                            defaultMessage: "Eliminar",
                          })}
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({
          id: "app.sidebar.socialMediaAutomatedPosts",
        })}
      />
      <Content className="p-0">
        <div className="social-auto">
          <div className="social-auto__top">
            <div className="social-auto__stepper">
              <div className="social-auto__stepLabel social-auto__stepLabel--project">
                {currentProjectName}
              </div>
              {([
                { id: "config", label: "1. Configuración" },
                { id: "media", label: "2. Crear contenido" },
                { id: "preview", label: "3. Vista previa" },
              ] as Array<{ id: StepId; label: string }>).map((s, i) => {
                const idx = i;
                const isActive = step === s.id;
                const isDone =
                  (step === "media" && idx <= 0) ||
                  (step === "preview" && idx <= 1);
                return (
                  <div
                    key={s.id}
                    className={cn("social-auto__step", {
                      "social-auto__step--active": isActive,
                      "social-auto__step--done": isDone,
                    })}
                  >
                    <div className="social-auto__stepCircle">{idx + 1}</div>
                    <div className="social-auto__stepLabel">{s.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="social-auto__subtitle">
              {intl.formatMessage({
                id: "app.socialAutomatedPosts.description",
                defaultMessage:
                  "Configura tu automatización: textos + imágenes generadas con IA. (Front mock)",
              })}
            </div>
          </div>

          <div className="social-auto__body">
            <div className="social-auto__panel social-auto__panel--right">
              {step === "config" && (
                <div className="social-auto__stepContent social-auto__stepContent--config">
                  <div className="social-auto__configModes">
                    <button
                      type="button"
                      className={cn("social-auto__configModeCard", {
                        "social-auto__configModeCard--active":
                          configMode === "guided",
                      })}
                      onClick={() => setConfigMode("guided")}
                    >
                      <span className="social-auto__configModeEyebrow">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.option1",
                          defaultMessage: "Opción 1",
                        })}
                      </span>
                      <span className="social-auto__configModeTitle">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.guided.title",
                          defaultMessage: "Industria y temáticas",
                        })}
                      </span>
                      <span className="social-auto__configModeHint">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.guided.hint",
                          defaultMessage:
                            "Define industria, temáticas e info adicional. La IA arma el resto.",
                        })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={cn("social-auto__configModeCard", {
                        "social-auto__configModeCard--active":
                          configMode === "custom",
                      })}
                      onClick={() => setConfigMode("custom")}
                    >
                      <span className="social-auto__configModeEyebrow">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.option2",
                          defaultMessage: "Opción 2",
                        })}
                      </span>
                      <span className="social-auto__configModeTitle">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.custom.title",
                          defaultMessage: "Contenido personalizado",
                        })}
                      </span>
                      <span className="social-auto__configModeHint">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.custom.hint",
                          defaultMessage:
                            "Describe el tipo de contenido y elige la temática de cada post.",
                        })}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={cn("social-auto__configModeCard", {
                        "social-auto__configModeCard--active":
                          configMode === "auto",
                      })}
                      onClick={() => setConfigMode("auto")}
                    >
                      <span className="social-auto__configModeEyebrow">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.option3",
                          defaultMessage: "Opción 3",
                        })}
                      </span>
                      <span className="social-auto__configModeTitle">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.auto.title",
                          defaultMessage: "Que la IA decida",
                        })}
                      </span>
                      <span className="social-auto__configModeHint">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.configMode.auto.hint",
                          defaultMessage:
                            "Solo explica qué hace tu marca. La IA elige la estrategia y los temas.",
                        })}
                      </span>
                    </button>
                  </div>

                  {configMode === "guided" ? (
                    <>
                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.industry",
                        defaultMessage: "1) Industria",
                      })}
                    </Label>
                    <Creatable
                      options={INDUSTRIES}
                      value={
                        industry
                          ? INDUSTRIES.find((opt) => opt.value === industry) ?? {
                              value: industry,
                              label: industry,
                            }
                          : null
                      }
                      className={cn(
                        "social-auto__reactSelect social-auto__reactSelect--modern",
                        {
                          "social-auto__reactSelect--invalid": errors.industry,
                        }
                      )}
                      classNamePrefix="select"
                      placeholder={intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.industryPlaceholder",
                        defaultMessage:
                          "Selecciona o escribe una industria personalizada",
                      })}
                      formatCreateLabel={(inputValue) =>
                        intl.formatMessage(
                          {
                            id: "app.socialAutomatedPosts.form.industryCreateLabel",
                            defaultMessage: 'Usar "{inputValue}" como industria',
                          },
                          { inputValue }
                        )
                      }
                      onChange={(value: any) =>
                        handleIndustryChange((value as Option | null)?.value ?? "")
                      }
                    />
                    {errors.industry && (
                      <div className="invalid-feedback">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.industry",
                          defaultMessage: "Requerido.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.topics",
                        defaultMessage: "2) Temáticas (selecciona varias)",
                      })}
                    </Label>
                    <Creatable
                      isMulti
                      options={TOPIC_TAGS}
                      value={topics}
                      className={cn("social-auto__reactSelect social-auto__reactSelect--modern", {
                        "social-auto__reactSelect--invalid": errors.topics,
                      })}
                      classNamePrefix="select"
                      placeholder={intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.topicsPlaceholder",
                        defaultMessage: "Busca, selecciona o escribe y pulsa Intro para agregar una temática personalizada",
                      })}
                      formatCreateLabel={(inputValue) =>
                        intl.formatMessage(
                          {
                            id: "app.socialAutomatedPosts.form.topicsCreateLabel",
                            defaultMessage: 'Agregar "{inputValue}"',
                          },
                          { inputValue }
                        )
                      }
                      onChange={(value: any) =>
                        setTopics((value as Option[]) ?? [])
                      }
                    />
                    {errors.topics && (
                      <div className="text-danger small mt-1">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.topicsReq",
                          defaultMessage: "Elige al menos 1 temática.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.additionalInfo",
                        defaultMessage: "3) Información adicional (opcional)",
                      })}
                    </Label>
                    <textarea
                      className="form-control social-auto__input"
                      rows={3}
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      placeholder={intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.additionalInfoPlaceholder",
                        defaultMessage:
                          "Datos de tu negocio, promociones, tono de marca, público objetivo, etc. La IA lo usará en textos e imágenes.",
                      })}
                    />
                  </FormGroup>
                    </>
                  ) : configMode === "custom" ? (
                    <>
                      <FormGroup className="social-auto__field">
                        <Label className="social-auto__label">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.customBrief",
                            defaultMessage:
                              "1) ¿Qué tipo de contenido quieres generar?",
                          })}
                        </Label>
                        <textarea
                          className={cn("form-control social-auto__input", {
                            "is-invalid": errors.customBrief,
                          })}
                          rows={3}
                          value={customContentBrief}
                          onChange={(e) => setCustomContentBrief(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.customBriefPlaceholder",
                            defaultMessage:
                              "Ej: tips educativos, promociones semanales, detrás de cámaras, lanzamiento de producto…",
                          })}
                        />
                        {errors.customBrief && (
                          <div className="text-danger small mt-1">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.customBriefReq",
                              defaultMessage:
                                "Describe el tipo de contenido que quieres generar.",
                            })}
                          </div>
                        )}
                      </FormGroup>
                    </>
                  ) : (
                    <>
                      <FormGroup className="social-auto__field">
                        <Label className="social-auto__label">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.brandDescription",
                            defaultMessage: "¿Qué hace tu marca?",
                          })}
                        </Label>
                        <textarea
                          className={cn("form-control social-auto__input", {
                            "is-invalid": errors.brandDescription,
                          })}
                          rows={4}
                          value={brandDescription}
                          onChange={(e) => setBrandDescription(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.brandDescriptionPlaceholder",
                            defaultMessage:
                              "Cuéntale a la IA a qué se dedica tu marca, a quién le habla, qué ofrece y qué tono usa. Ella definirá la estrategia y los temas de cada post.",
                          })}
                        />
                        {errors.brandDescription && (
                          <div className="text-danger small mt-1">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.brandDescriptionReq",
                              defaultMessage:
                                "Explica qué hace tu marca para que la IA pueda decidir la estrategia.",
                            })}
                          </div>
                        )}
                        <div className="text-muted small mt-2">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.brandDescriptionHint",
                            defaultMessage:
                              "No necesitas elegir temáticas: la IA propondrá un mix de presentación, tips, prueba social, CTA y más.",
                          })}
                        </div>
                      </FormGroup>
                    </>
                  )}

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.includeLogo",
                        defaultMessage: "Incluir logo de marca",
                      })}
                    </Label>
                    <label className="social-auto__logoToggle">
                      <input
                        type="checkbox"
                        checked={includeLogo}
                        onChange={(e) => setIncludeLogo(e.target.checked)}
                      />
                      <span>
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.includeLogoHint",
                          defaultMessage:
                            "Activado por defecto. Si lo desactivas, la IA generará sin usar el logo.",
                        })}
                      </span>
                    </label>
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.contentType",
                        defaultMessage: "Tipo de contenido a generar",
                      })}
                    </Label>
                    <div className="social-auto__contentTypes">
                      {CONTENT_TYPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={cn("social-auto__contentTypeBtn", {
                            "social-auto__contentTypeBtn--active":
                              contentType === opt.value,
                          })}
                          onClick={() => setContentType(opt.value)}
                        >
                          <span className="social-auto__contentTypeLabel">
                            {opt.label}
                          </span>
                          <span className="social-auto__contentTypeHint">
                            {intl.formatMessage({
                              id: opt.hintKey,
                              defaultMessage: opt.hintDefault,
                            })}
                          </span>
                        </button>
                      ))}
                    </div>
                  </FormGroup>

                  {contentType !== "text" ? (
                    <>
                      <FormGroup className="social-auto__field">
                        <Label className="social-auto__label">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.imageAspect",
                            defaultMessage: "Formato de la imagen",
                          })}
                        </Label>
                        <div className="social-auto__contentTypes">
                          {IMAGE_ASPECT_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={cn("social-auto__contentTypeBtn", {
                                "social-auto__contentTypeBtn--active":
                                  imageAspect === opt.value,
                              })}
                              onClick={() => setImageAspect(opt.value)}
                            >
                              <span
                                className={cn("social-auto__aspectPreview", {
                                  "social-auto__aspectPreview--square":
                                    opt.value === "1:1",
                                  "social-auto__aspectPreview--portrait":
                                    opt.value === "2:3",
                                })}
                                aria-hidden
                              >
                                <span className="social-auto__aspectFrame">
                                  <span className="social-auto__aspectFrameMedia" />
                                  <span className="social-auto__aspectFrameLines">
                                    <span />
                                    <span />
                                    <span />
                                  </span>
                                </span>
                              </span>
                              <span className="social-auto__contentTypeLabel">
                                {opt.label}
                              </span>
                              <span className="social-auto__contentTypeHint">
                                {opt.hint}
                              </span>
                            </button>
                          ))}
                        </div>
                      </FormGroup>

                      <FormGroup className="social-auto__field">
                        <Label className="social-auto__label">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.imageTextDensity",
                            defaultMessage: "Texto dentro de la imagen",
                          })}
                        </Label>
                        <div className="social-auto__contentTypes">
                          {IMAGE_TEXT_DENSITY_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={cn("social-auto__contentTypeBtn", {
                                "social-auto__contentTypeBtn--active":
                                  imageTextDensity === opt.value,
                              })}
                              onClick={() => setImageTextDensity(opt.value)}
                            >
                              <span className="social-auto__contentTypeLabel">
                                {opt.label}
                              </span>
                              <span className="social-auto__contentTypeHint">
                                {opt.hint}
                              </span>
                            </button>
                          ))}
                        </div>
                      </FormGroup>
                    </>
                  ) : null}

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.duration",
                        defaultMessage: "5) ¿Por cuánto tiempo? (máx. un mes)",
                      })}
                    </Label>
                    <Input
                      type="select"
                      value={String(durationWeeks)}
                      onChange={(e) => setDurationWeeks(Number(e.target.value))}
                      className="social-auto__input"
                    >
                      {DURATION_OPTIONS.map((opt) => (
                        <option value={String(opt.value)} key={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    <div className="text-muted small mt-1">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.durationHint",
                        defaultMessage:
                          "Se generan como máximo 2 publicaciones por semana.",
                      })}
                    </div>
                  </FormGroup>

                  <FormGroup className="social-auto__field">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.days",
                        defaultMessage: "6) Días (máx. 2)",
                      })}
                    </Label>
                    <div className="social-auto__days">
                      {DAYS.map((d) => {
                        const active = selectedDays.includes(d.key);
                        return (
                          <button
                            key={d.key}
                            type="button"
                            className={cn(
                              "social-auto__dayBtn",
                              active && "social-auto__dayBtn--active"
                            )}
                            onClick={() => toggleDay(d.key)}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.days && (
                      <div className="text-danger small mt-1">
                        {selectedDays.length > 2
                          ? intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.daysMax",
                              defaultMessage:
                                "Elige máximo 2 días por semana.",
                            })
                          : intl.formatMessage({
                              id: "app.socialAutomatedPosts.error.daysReq",
                              defaultMessage:
                                "Selecciona al menos 1 día.",
                            })}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="social-auto__field social-auto__field--date">
                    <Label className="social-auto__label">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.startDate",
                        defaultMessage: "Fecha de inicio",
                      })}
                    </Label>
                    <DatePicker
                      className={cn("w-100 social-auto__datePicker", {
                        "is-invalid": errors.startDate,
                      })}
                      calendarClassName="social-auto__calendar"
                      format="dd/MM/y"
                      dayPlaceholder="dd"
                      monthPlaceholder="mm"
                      yearPlaceholder="aaaa"
                      minDate={new Date()}
                      value={startDate}
                      onChange={(d: any) => setStartDate(toDate(d))}
                      calendarIcon={
                        <FontAwesomeIcon
                          icon={["far", "calendar"]}
                          className="social-auto__datePickerIcon"
                        />
                      }
                    />
                    <div className="text-muted small mt-1">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.startDateHint",
                        defaultMessage:
                          "Desde esta fecha se armará el calendario sugerido.",
                      })}
                    </div>
                    {errors.startDate && (
                      <div className="text-danger small mt-1">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.error.startDateReq",
                          defaultMessage: "Selecciona una fecha.",
                        })}
                      </div>
                    )}
                  </FormGroup>

                  {configMode === "custom" && (
                    <FormGroup className="social-auto__field">
                      <Label className="social-auto__label">
                        {intl.formatMessage(
                          {
                            id: "app.socialAutomatedPosts.form.customPostTopics",
                            defaultMessage:
                              "Temática de cada publicación ({count})",
                          },
                          { count: Math.max(previewDates.length, 0) }
                        )}
                      </Label>
                      <div className="text-muted small mb-2">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.customPostTopicsHint",
                          defaultMessage:
                            "Define qué quieres comunicar en cada post del calendario planificado.",
                        })}
                      </div>
                      {previewDates.length === 0 ? (
                        <div className="social-auto__customTopicsEmpty text-muted">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.customPostTopicsEmpty",
                            defaultMessage:
                              "Selecciona días, duración y fecha de inicio para habilitar las temáticas por post.",
                          })}
                        </div>
                      ) : (
                        <div className="social-auto__customTopics">
                          {previewDates.map((date, idx) => (
                            <div
                              key={date.toISOString()}
                              className="social-auto__customTopicRow"
                            >
                              <span className="social-auto__customTopicIndex">
                                {intl.formatMessage(
                                  {
                                    id: "app.socialAutomatedPosts.form.customPostLabel",
                                    defaultMessage: "Post {n}",
                                  },
                                  { n: idx + 1 }
                                )}
                                <small>
                                  {date.toLocaleDateString(intl.locale || "es", {
                                    weekday: "short",
                                    day: "2-digit",
                                    month: "short",
                                  })}
                                </small>
                              </span>
                              <div className="social-auto__customTopicInputWrap">
                                <button
                                  type="button"
                                  className={cn(
                                    "social-auto__topicIdeasWand",
                                    {
                                      "social-auto__topicIdeasWand--busy":
                                        topicIdeasLoadingIndex === idx,
                                      "social-auto__topicIdeasWand--open":
                                        topicIdeasOpenIndex === idx,
                                    }
                                  )}
                                  title={intl.formatMessage({
                                    id: "app.socialAutomatedPosts.topicIdeas.wandTitle",
                                    defaultMessage:
                                      "Sugerir temáticas con IA (rápido, sin créditos)",
                                  })}
                                  aria-label={intl.formatMessage({
                                    id: "app.socialAutomatedPosts.topicIdeas.wandTitle",
                                    defaultMessage:
                                      "Sugerir temáticas con IA (rápido, sin créditos)",
                                  })}
                                  disabled={topicIdeasLoadingIndex != null}
                                  onClick={() => {
                                    if (
                                      topicIdeasOpenIndex === idx &&
                                      (topicIdeasByIndex[idx] || []).length
                                    ) {
                                      setTopicIdeasOpenIndex(null);
                                      return;
                                    }
                                    void requestTopicIdeas(idx);
                                  }}
                                >
                                  {topicIdeasLoadingIndex === idx ? (
                                    <Spinner size="sm" color="primary" />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={["fas", "wand-magic-sparkles"]}
                                    />
                                  )}
                                </button>
                                <Input
                                  type="text"
                                  className={cn("social-auto__input", {
                                    "is-invalid":
                                      errors.customTopics &&
                                      !customPostTopics[idx]?.trim(),
                                  })}
                                  value={customPostTopics[idx] || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setCustomPostTopics((prev) => {
                                      const next = [...prev];
                                      next[idx] = value;
                                      return next;
                                    });
                                  }}
                                  placeholder={intl.formatMessage({
                                    id: "app.socialAutomatedPosts.form.customPostTopicPlaceholder",
                                    defaultMessage:
                                      "Ej: Promo 2x1, tip del día, testimonio…",
                                  })}
                                />
                                {topicIdeasOpenIndex === idx ? (
                                  <div
                                    className="social-auto__topicIdeasPanel"
                                    role="listbox"
                                    aria-label={intl.formatMessage({
                                      id: "app.socialAutomatedPosts.topicIdeas.panelLabel",
                                      defaultMessage: "Ideas de temática",
                                    })}
                                  >
                                    <div className="social-auto__topicIdeasPanelHead">
                                      <span>
                                        {intl.formatMessage({
                                          id: "app.socialAutomatedPosts.topicIdeas.panelTitle",
                                          defaultMessage:
                                            "Ideas rápidas · sin créditos",
                                        })}
                                      </span>
                                      <button
                                        type="button"
                                        className="social-auto__topicIdeasClose"
                                        onClick={() =>
                                          setTopicIdeasOpenIndex(null)
                                        }
                                      >
                                        ×
                                      </button>
                                    </div>
                                    {topicIdeasLoadingIndex === idx &&
                                    !(topicIdeasByIndex[idx] || []).length ? (
                                      <div className="social-auto__topicIdeasLoading">
                                        {intl.formatMessage({
                                          id: "app.socialAutomatedPosts.topicIdeas.loading",
                                          defaultMessage:
                                            "Buscando ángulos…",
                                        })}
                                      </div>
                                    ) : (
                                      <ul className="social-auto__topicIdeasList">
                                        {(topicIdeasByIndex[idx] || []).map(
                                          (idea) => (
                                            <li key={idea}>
                                              <button
                                                type="button"
                                                className="social-auto__topicIdeaBtn"
                                                onClick={() =>
                                                  applyTopicIdea(idx, idea)
                                                }
                                              >
                                                {idea}
                                              </button>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    )}
                                    {(topicIdeasByIndex[idx] || []).length >
                                    0 ? (
                                      <button
                                        type="button"
                                        className="social-auto__topicIdeasRefresh"
                                        disabled={
                                          topicIdeasLoadingIndex != null
                                        }
                                        onClick={() =>
                                          void requestTopicIdeas(idx)
                                        }
                                      >
                                        {intl.formatMessage({
                                          id: "app.socialAutomatedPosts.topicIdeas.refresh",
                                          defaultMessage: "Otras ideas",
                                        })}
                                      </button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {errors.customTopics && (
                        <div className="text-danger small mt-1">
                          {errors.customTopics === "incomplete"
                            ? intl.formatMessage({
                                id: "app.socialAutomatedPosts.error.customTopicsIncomplete",
                                defaultMessage:
                                  "Completa la temática de todos los posts planificados.",
                              })
                            : intl.formatMessage({
                                id: "app.socialAutomatedPosts.error.customTopicsReq",
                                defaultMessage:
                                  "Define al menos una temática para tus posts.",
                              })}
                        </div>
                      )}
                    </FormGroup>
                  )}

                  <div className="social-auto__smartSchedule">
                    <button
                      type="button"
                      className="social-auto__smartScheduleHeader"
                      onClick={() => setSmartScheduleOpen((o) => !o)}
                      aria-expanded={smartScheduleOpen}
                    >
                      <span className="social-auto__smartScheduleTitle">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.smartSchedule.title",
                          defaultMessage: "Utilizar un horario inteligente",
                        })}
                      </span>
                      <span
                        className={cn("social-auto__smartScheduleChevron", {
                          "social-auto__smartScheduleChevron--open": smartScheduleOpen,
                        })}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>
                    {smartScheduleOpen && (
                      <div className="social-auto__smartScheduleBody">
                        <div className="social-auto__smartScheduleAlert">
                          <span className="social-auto__smartScheduleAlertIcon" aria-hidden>i</span>
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.smartSchedule.hint",
                              defaultMessage:
                                "Los horarios recomendados se basan en el análisis de engagement de tu audiencia en las últimas 4 semanas.",
                            })}
                          </span>
                        </div>
                        <div className="social-auto__smartScheduleTableWrap">
                          <table className="social-auto__smartScheduleTable">
                            <thead>
                              <tr>
                                <th>
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.smartSchedule.colTime",
                                    defaultMessage: "Horarios recomendados",
                                  })}
                                </th>
                                <th>
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.smartSchedule.colEngagement",
                                    defaultMessage: "Engagement",
                                  })}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {smartScheduleSuggestions.map((row, idx) => (
                                <tr
                                  key={row.time + row.label}
                                  role="button"
                                  tabIndex={0}
                                  className={cn(
                                    "social-auto__smartScheduleRow",
                                    idx === 0 && "social-auto__smartScheduleRow--top",
                                    selectedSmartTime === row.time && "social-auto__smartScheduleRow--selected"
                                  )}
                                  onClick={() => {
                                    setSelectedSmartTime(row.time);
                                    toast.success(
                                      intl.formatMessage(
                                        {
                                          id: "app.socialAutomatedPosts.smartSchedule.applied",
                                          defaultMessage: "Horario aplicado: {time}. Se verá en el calendario.",
                                        },
                                        { time: row.label }
                                      )
                                    );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setSelectedSmartTime(row.time);
                                      toast.success(
                                        intl.formatMessage(
                                          {
                                            id: "app.socialAutomatedPosts.smartSchedule.applied",
                                            defaultMessage: "Horario aplicado: {time}. Se verá en el calendario.",
                                          },
                                          { time: row.label }
                                        )
                                      );
                                    }
                                  }}
                                >
                                  <td>{row.label}</td>
                                  <td>
                                    <span className="social-auto__smartSchedulePct">
                                      {row.engagement}
                                    </span>
                                    <span className="social-auto__smartScheduleStars">
                                      {"★".repeat(row.stars)}
                                      {"☆".repeat(5 - row.stars)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="social-auto__smartScheduleFooter">
                          <Button
                            color="primary"
                            outline
                            size="sm"
                            onClick={() =>
                              setShowAllSmartHours((prev) => !prev)
                            }
                          >
                            {showAllSmartHours
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.smartSchedule.showLess",
                                  defaultMessage: "Mostrar menos horas",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.smartSchedule.seeAll",
                                  defaultMessage: "Ver todas las horas",
                                })}
                            {" "}
                            <span aria-hidden>
                              {showAllSmartHours ? "▲" : "▼"}
                            </span>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween social-auto__actions--config">
                    <Button color="link" className="social-auto__backBtn" onClick={backToProjectList}>
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.backToProjects",
                        defaultMessage: "Volver a proyectos",
                      })}
                    </Button>
                    <Button color="primary" className="social-auto__primaryBtn" onClick={validateAndNext}>
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.nextToMedia",
                        defaultMessage: "Continuar: Crear contenido",
                      })}
                    </Button>
                  </div>
                </div>
              )}

              {step === "media" && (
                <div className="social-auto__stepContent">
                  <div className="social-auto__mediaHeader">
                    <div className="social-auto__mediaTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.mediaTitle",
                        defaultMessage: "Crear contenido",
                      })}
                    </div>
                    <div className="text-muted small">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.form.mediaHint",
                        defaultMessage:
                          "Opcional: sube tu logo y fotos de referencia. La IA (OpenAI) los usará para diseñar las piezas. Si no subes nada, generará el diseño solo con la descripción.",
                      })}
                    </div>
                  </div>

                  <div className="social-auto__uploadGrid">
                    <div className="social-auto__uploadCol">
                    {includeLogo ? (
                    <div className="social-auto__uploadBox">
                      <div className="social-auto__uploadLabel">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.logo",
                          defaultMessage: "Logo de la empresa (opcional)",
                        })}
                      </div>
                      <div
                        className={cn("social-auto__drop", {
                          "social-auto__drop--invalid": Boolean(errors.logo),
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const el = document.getElementById(
                            "logo-input"
                          ) as HTMLInputElement | null;
                          el?.click();
                        }}
                      >
                        {logo ? (
                          <img
                            src={logo.previewUrl}
                            alt="logo"
                            className="social-auto__dropImg"
                          />
                        ) : (
                          <div className="social-auto__dropPlaceholder">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.upload.logo",
                              defaultMessage: "Click para subir logo",
                            })}
                          </div>
                        )}
                      </div>

                      <input
                        id="logo-input"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) =>
                          onLogoSelected(e.target.files?.[0] ?? null)
                        }
                      />
                      {errors.logo && (
                        <div className="text-danger small mt-1">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.error.logoReq",
                            defaultMessage: "Logo requerido.",
                          })}
                        </div>
                      )}
                    </div>
                    ) : (
                      <div className="social-auto__uploadBox social-auto__uploadBox--muted">
                        <div className="social-auto__uploadLabel">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.logoDisabled",
                            defaultMessage: "Logo desactivado",
                          })}
                        </div>
                        <div className="text-muted small">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.logoDisabledHint",
                            defaultMessage:
                              "Activaste generar sin logo. Puedes cambiarlo en la configuración.",
                          })}
                        </div>
                      </div>
                    )}

                    <div className="social-auto__uploadBox">
                      <div className="social-auto__uploadLabel">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.marketingLine",
                          defaultMessage: "Línea de marketing a seguir",
                        })}
                      </div>
                      <div className="text-muted small mb-2">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.marketingLineHint",
                          defaultMessage:
                            "Sube imágenes de inspiración (moodboard) para que la IA siga la misma línea gráfica en todas las piezas.",
                        })}
                      </div>
                      <div
                        className={cn(
                          "social-auto__imagesDropZone social-auto__imagesDropZone--compact",
                          isDraggingMarketingLine &&
                            "social-auto__imagesDropZone--drag"
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          const el = document.getElementById(
                            "marketing-line-input"
                          ) as HTMLInputElement | null;
                          el?.click();
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          setIsDraggingMarketingLine(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingMarketingLine(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingMarketingLine(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingMarketingLine(false);
                          pickMarketingLineImages(e.dataTransfer.files);
                        }}
                      >
                        <div className="social-auto__imagesDropZoneTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.marketingLineDrop",
                            defaultMessage: "Arrastra referencias de estilo",
                          })}
                        </div>
                        <div className="social-auto__imagesDropZoneHint text-muted small">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.form.marketingLineDropHint",
                            defaultMessage: `O clic para subir (máx. ${MAX_MARKETING_LINE_IMAGES}).`,
                          })}
                        </div>
                      </div>
                      <input
                        id="marketing-line-input"
                        type="file"
                        accept="image/*"
                        multiple
                        className="d-none"
                        onChange={(e) => {
                          pickMarketingLineImages(e.target.files);
                          e.target.value = "";
                        }}
                      />
                      <div className="social-auto__marketingLineGrid">
                        {marketingLineImages.map((img, idx) => (
                          <div
                            key={idx}
                            className={cn("social-auto__imgSlot", {
                              "social-auto__imgSlot--filled": Boolean(img),
                            })}
                          >
                            <div className="social-auto__imgSlotHeader">
                              <span>#{idx + 1}</span>
                              {img && (
                                <button
                                  type="button"
                                  className="social-auto__imgSlotRemove"
                                  onClick={() => clearMarketingLineSlot(idx)}
                                >
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.remove",
                                    defaultMessage: "Quitar",
                                  })}
                                </button>
                              )}
                            </div>
                            <div
                              className="social-auto__imgSlotDrop"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                const el = document.getElementById(
                                  "marketing-line-input"
                                ) as HTMLInputElement | null;
                                el?.click();
                              }}
                            >
                              {img ? (
                                <img
                                  src={img.previewUrl}
                                  alt={`marketing-line-${idx + 1}`}
                                  className="social-auto__imgSlotImg"
                                />
                              ) : (
                                <div className="social-auto__imgSlotPlaceholder">
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.upload.image",
                                    defaultMessage: "Subir imagen",
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    </div>

                    <div className="social-auto__uploadBox">
                      <div className="social-auto__uploadLabel">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.form.images",
                          defaultMessage:
                            "Hasta 5 imágenes de referencia (opcional)",
                        })}
                      </div>

                      <div
                        className={cn(
                          "social-auto__imagesDropZone",
                          isDraggingImages &&
                            "social-auto__imagesDropZone--drag"
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setReplaceSlotIndex(null);
                          const el = document.getElementById(
                            "images-input"
                          ) as HTMLInputElement | null;
                          el?.click();
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingImages(false);
                          const files = e.dataTransfer.files;
                          pickImagesForSlots(files, replaceSlotIndex);
                          setReplaceSlotIndex(null);
                        }}
                      >
                        <div className="social-auto__imagesDropZoneTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.drop.title",
                            defaultMessage:
                              "Arrastra o Suelta imagenes aquí",
                          })}
                        </div>
                        <div className="social-auto__imagesDropZoneHint text-muted small">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.drop.hint",
                            defaultMessage:
                              "O clic para seleccionar (máx. 5). Mín. 3 para previsualizar.",
                          })}
                        </div>
                      </div>

                      <div className="social-auto__imagesGrid">
                        {images.map((img, idx) => (
                          <div
                            key={idx}
                            className={cn("social-auto__imgSlot", {
                              "social-auto__imgSlot--filled": Boolean(img),
                              "social-auto__imgSlot--invalid": Boolean(
                                errors.images
                              ),
                            })}
                          >
                            <div className="social-auto__imgSlotHeader">
                              <span>#{idx + 1}</span>
                              {img && (
                                <button
                                  type="button"
                                  className="social-auto__imgSlotRemove"
                                  onClick={() => clearSlot(idx)}
                                >
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.remove",
                                    defaultMessage: "Quitar",
                                  })}
                                </button>
                              )}
                            </div>

                            <div
                              className="social-auto__imgSlotDrop"
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setReplaceSlotIndex(idx);
                                const el = document.getElementById(
                                  "images-input"
                                ) as HTMLInputElement | null;
                                el?.click();
                              }}
                            >
                              {img ? (
                                <img
                                  src={img.previewUrl}
                                  alt={`img ${idx + 1}`}
                                  className="social-auto__imgSlotImg"
                                />
                              ) : (
                                <div className="social-auto__imgSlotPlaceholder">
                                  {intl.formatMessage({
                                    id: "app.socialAutomatedPosts.upload.image",
                                    defaultMessage: "Elegir",
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <input
                        id="images-input"
                        type="file"
                        accept="image/*"
                        className="d-none"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          pickImagesForSlots(files, replaceSlotIndex);
                          setReplaceSlotIndex(null);
                          // reset input so selecting same file again works
                          e.currentTarget.value = "";
                        }}
                      />

                      {errors.images && (
                        <div className="text-danger small mt-1">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.error.imagesReq",
                            defaultMessage:
                              "Sube entre 3 y 5 imágenes.",
                          })}
                          {errors.images === "requiredMin"
                            ? ` (mínimo ${MIN_CONTENT_IMAGES})`
                            : errors.images === "requiredMax"
                              ? ` (máximo ${MAX_CONTENT_IMAGES})`
                              : null}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween">
                    <Button
                      color="link"
                      onClick={() => {
                        void persistCurrentDraft();
                        setStep("config");
                      }}
                    >
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.back",
                        defaultMessage: "Volver",
                      })}
                    </Button>
                    {hasGeneratedContent || isGeneratingContent ? (
                      <Button
                        color="primary"
                        onClick={() => {
                          setStep("preview");
                          persistCurrentDraft();
                        }}
                      >
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.goToPreview",
                          defaultMessage: "Ir a vista previa",
                        })}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        onClick={() => setIsGenerationConfirmOpen(true)}
                        disabled={isGeneratingContent}
                      >
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.createContent",
                          defaultMessage: "Crear contenido",
                        })}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {step === "preview" && (
                <div className="social-auto__stepContent">
                  <div className="social-auto__previewIntro">
                    <div className="social-auto__previewIntroTitle">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.preview.title",
                        defaultMessage: "Previsualización de publicaciones",
                      })}
                    </div>
                    <div className="social-auto__previewIntroActions">
                      {isGeneratingContent ? (
                        <Button
                          color="danger"
                          outline
                          size="sm"
                          onClick={stopGeneration}
                        >
                          {intl.formatMessage(
                            {
                              id: "app.socialAutomatedPosts.preview.stop",
                              defaultMessage: "Detener ({done}/{total})",
                            },
                            {
                              done: jobState?.done ?? 0,
                              total: jobState?.total ?? 0,
                            }
                          )}
                        </Button>
                      ) : (
                        <Button
                          color="outline-secondary"
                          size="sm"
                          onClick={() => startGeneration(false)}
                          disabled={isGenerating}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.preview.regenerate",
                            defaultMessage: "Regenerar contenido con IA",
                          })}
                        </Button>
                      )}
                    </div>
                  </div>

                  {isGeneratingContent ? (
                    <div className="social-auto__genLoader" role="status">
                      <Spinner color="primary" size="sm" />
                      <div className="social-auto__genLoaderBody">
                        <div className="social-auto__genLoaderTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.generate.loadingTitle",
                            defaultMessage: "Estamos generando el contenido con la IA de Wembii…",
                          })}
                        </div>
                        <div className="social-auto__genLoaderText text-muted small">
                          {intl.formatMessage(
                            {
                              id: "app.socialAutomatedPosts.generate.loadingText",
                              defaultMessage:
                                "{done} de {total} listas · {elapsed}s · ~{eta}s restantes",
                            },
                            {
                              done: generationStats.done,
                              total: generationStats.total,
                              elapsed: generationStats.elapsedSec,
                              eta: generationStats.etaSec,
                            }
                          )}
                        </div>
                        {(showBackgroundWaitTip ||
                          generationStats.elapsedSec >= 10) && (
                          <div className="social-auto__genLoaderTip">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.background.waitTip",
                              defaultMessage:
                                "Esto está demorando. Podés cerrar la ventana: seguimos trabajando en segundo plano.",
                            })}
                          </div>
                        )}
                      </div>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={stopGeneration}
                      >
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.generate.stopBtn",
                          defaultMessage: "Detener",
                        })}
                      </Button>
                    </div>
                  ) : null}

                  <div className="social-auto__calendarWrap">
                    <div className="social-auto__calendarHeader">
                      <div className="social-auto__calendarMonthControl">
                        <Button
                          color="link"
                          size="sm"
                          className="social-auto__calendarNavBtn"
                          onClick={() =>
                            setCalendarMonthOffset((offset) => offset - 1)
                          }
                          aria-label="Mes anterior"
                        >
                          ‹
                        </Button>
                        <div className="social-auto__calendarTitle">
                          {calendarModel.title}
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="social-auto__calendarNavBtn"
                          onClick={() =>
                            setCalendarMonthOffset((offset) => offset + 1)
                          }
                          aria-label="Mes siguiente"
                        >
                          ›
                        </Button>
                        {calendarMonthOffset !== 0 ? (
                          <Button
                            color="link"
                            size="sm"
                            className="social-auto__calendarTodayBtn"
                            onClick={() => setCalendarMonthOffset(0)}
                          >
                            Volver al mes del plan
                          </Button>
                        ) : null}
                      </div>
                      <div className="social-auto__calendarHint text-muted small">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.calendar.hint",
                          defaultMessage:
                            "Seleccioná las publicaciones que querés programar. Las programadas quedan marcadas en el calendario.",
                        })}
                      </div>

                      {approvalMode ? (
                        <div className="social-auto__approvalMasterInCalendar">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (!checked) {
                                setApprovedPostIds({});
                                return;
                              }
                              const next: Record<string, boolean> = {};
                              previewPosts.forEach((p) => {
                                next[p.id] = true;
                              });
                              setApprovedPostIds(next);
                            }}
                          />
                          <span>
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.schedule.selectAll",
                              defaultMessage: "Seleccionar todo",
                            })}
                          </span>
                          <span className="text-muted small ml-2">
                            {selectedCount}/{previewPosts.length}{" "}
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.schedule.selected",
                              defaultMessage: "seleccionados",
                            })}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <div className="social-auto__calendarGrid">
                      {calendarModel.weekDays.map((d) => (
                        <div key={d} className="social-auto__calendarDow">
                          {d}
                        </div>
                      ))}

                      {calendarModel.cells.map((cell, idx) => {
                        const post = cell.key ? calendarModel.daysByKey.get(cell.key) : undefined;
                        const hasPost = Boolean(post);
                        return (
                          <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${cell.key ?? "empty"}-${idx}`}
                            className={cn("social-auto__calendarCell", {
                              "social-auto__calendarCell--empty": !cell.key,
                              "social-auto__calendarCell--hasPost": hasPost,
                              "social-auto__calendarCell--scheduled":
                                Boolean(post && scheduledPostIds[post.id]),
                              "social-auto__calendarCell--selected": Boolean(
                                post && approvalMode && approvedPostIds[post.id]
                              ),
                            })}
                            role={post ? "button" : undefined}
                            tabIndex={post ? 0 : undefined}
                            onClick={() => {
                              if (!post) return;
                              if (approvalMode) {
                                setApprovedPostIds((prev) => ({
                                  ...prev,
                                  [post.id]: !prev[post.id],
                                }));
                                return;
                              }
                              setActivePreviewPost(post);
                              setActiveSocialPreview("instagram");
                              setNanoEditPrompt("");
                              setTextChangePrompt("");
                              setNanoEditProgress(0);
                              setIsEditingNanoBanana(false);
                              setIsPostModalOpen(true);
                            }}
                          >
                            {cell.dayNumber != null && (
                              <div className="social-auto__calendarDayNumber">
                                {cell.dayNumber}
                              </div>
                            )}

                            {post?.image ? (
                              <div className="social-auto__calendarPostCard">
                                <div className="social-auto__calendarPostCardHeader">
                                  {logo?.previewUrl ? (
                                    <img
                                      src={logo.previewUrl}
                                      alt=""
                                      className="social-auto__calendarPostCardAvatar"
                                    />
                                  ) : (
                                    <span className="social-auto__calendarPostCardAvatar social-auto__calendarPostCardAvatar--placeholder">
                                      {(companyName || currentProjectName || "W")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                  <span className="social-auto__calendarPostCardName">
                                    {companyName || currentProjectName || "Tu negocio"}
                                  </span>
                                </div>
                                <img
                                  src={post.image}
                                  alt="scheduled"
                                  className="social-auto__calendarCellImg"
                                />
                                {post.text && !isPlaceholderCaption(post.text) ? (
                                  <div className="social-auto__calendarPostCardCaption">
                                    {post.text}
                                  </div>
                                ) : null}
                              </div>
                            ) : post ? (
                              <div className="social-auto__calendarPostCard social-auto__calendarPostCard--textOnly">
                                <div className="social-auto__calendarPostCardHeader">
                                  {logo?.previewUrl ? (
                                    <img
                                      src={logo.previewUrl}
                                      alt=""
                                      className="social-auto__calendarPostCardAvatar"
                                    />
                                  ) : (
                                    <span className="social-auto__calendarPostCardAvatar social-auto__calendarPostCardAvatar--placeholder">
                                      {(companyName || currentProjectName || "W")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                  <span className="social-auto__calendarPostCardName">
                                    {companyName || currentProjectName || "Tu negocio"}
                                  </span>
                                </div>
                                <div className="social-auto__calendarPostCardCaption">
                                  {post.text && !isPlaceholderCaption(post.text)
                                    ? post.text
                                    : post.title}
                                </div>
                              </div>
                            ) : null}

                            {post &&
                            (editingImagePostIds[post.id] ||
                              isPostStillGenerating(post.id)) ? (
                              <div className="social-auto__calendarCellLoading">
                                <Spinner color="primary" size="sm" />
                                {editingImagePostIds[post.id] ? (
                                  <span className="social-auto__calendarCellLoadingLabel">
                                    {intl.formatMessage({
                                      id: "app.socialAutomatedPosts.generate.cellEditing",
                                      defaultMessage: "Editando diseño…",
                                    })}
                                  </span>
                                ) : (
                                  <>
                                    <span className="social-auto__calendarCellLoadingLabel">
                                      {intl.formatMessage({
                                        id: "app.socialAutomatedPosts.generate.cellLoading",
                                        defaultMessage: "Generando…",
                                      })}
                                    </span>
                                    {(() => {
                                      const eta = etaSecForPost(post.id);
                                      if (!eta) return null;
                                      return (
                                        <span className="social-auto__calendarCellLoadingEta">
                                          {intl.formatMessage(
                                            {
                                              id: "app.socialAutomatedPosts.generate.cellEta",
                                              defaultMessage: "~{eta}s",
                                            },
                                            { eta }
                                          )}
                                        </span>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                            ) : null}

                            {post && !isPostStillGenerating(post.id) ? (
                              <div className="social-auto__calendarCellOverlay">
                                <div className="social-auto__calendarCellOverlayTime">
                                  {post.time}
                                </div>
                                {jobState?.results[post.id]?.error &&
                                !post.image ? (
                                  <div className="social-auto__calendarCellBadge social-auto__calendarCellBadge--error">
                                    {intl.formatMessage({
                                      id: "app.socialAutomatedPosts.generate.imageFailed",
                                      defaultMessage: "Sin diseño",
                                    })}
                                  </div>
                                ) : scheduledPostIds[post.id] ? (
                                  <div className="social-auto__calendarCellBadge social-auto__calendarCellBadge--scheduled">
                                    {intl.formatMessage({
                                      id: "app.socialAutomatedPosts.schedule.badge",
                                      defaultMessage: "Programado",
                                    })}
                                  </div>
                                ) : (
                                  <div
                                    className="social-auto__calendarCellOverlayAI"
                                    title={intl.formatMessage({
                                      id: "app.socialAutomatedPosts.confidence.tooltip",
                                      defaultMessage:
                                        "Índice de aptitud de la pieza según la IA. Resume coherencia con tu marca, claridad del mensaje y potencial de engagement antes de publicar.",
                                    })}
                                  >
                                    {intl.formatMessage(
                                      {
                                        id: "app.socialAutomatedPosts.confidence.label",
                                        defaultMessage: "AI: {pct}%",
                                      },
                                      {
                                        pct: Math.round(post.confidence * 100),
                                      }
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : post ? (
                              <div className="social-auto__calendarCellOverlay">
                                <div className="social-auto__calendarCellOverlayTime">
                                  {post.time}
                                </div>
                              </div>
                            ) : null}

                            {approvalMode && post ? (
                              <div
                                className="social-auto__calendarCellApproval"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={Boolean(approvedPostIds[post.id])}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setApprovedPostIds((prev) => ({
                                      ...prev,
                                      [post.id]: checked,
                                    }));
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="social-auto__actions social-auto__actions--spaceBetween">
                    <Button
                      color="link"
                      onClick={() => {
                        void persistCurrentDraft();
                        setStep("media");
                      }}
                    >
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.backToMedia",
                        defaultMessage: "Volver",
                      })}
                    </Button>

                    {!approvalMode ? (
                      <Button
                        color="primary"
                        onClick={() => {
                          setApprovalMode(true);
                          setApprovedPostIds({});
                        }}
                        disabled={isGenerating || !hasGeneratedContent}
                      >
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.toApproval",
                          defaultMessage: "Programar en las redes sociales",
                        })}
                      </Button>
                    ) : (
                      <div
                        className="d-flex align-items-center flex-wrap"
                        style={{ gap: 12 }}
                      >
                        <Button
                          color="link"
                          onClick={() => {
                            setApprovalMode(false);
                            setApprovedPostIds({});
                          }}
                          disabled={isGenerating}
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.schedule.cancel",
                            defaultMessage: "Cancelar",
                          })}
                        </Button>

                        <Button
                          color="primary"
                          onClick={openScheduleNetworksModal}
                          disabled={isGenerating || !canSendToCalendar}
                        >
                          {isGenerating
                            ? intl.formatMessage({
                                id: "app.socialAutomatedPosts.creating",
                                defaultMessage: "Programando...",
                              })
                            : intl.formatMessage(
                                {
                                  id: "app.socialAutomatedPosts.sendCalendar",
                                  defaultMessage:
                                    "Programar seleccionados ({count})",
                                },
                                { count: selectedCount }
                              )}
                        </Button>
                      </div>
                    )}
                  </div>

                </div>
              )}

              <Modal isOpen={isPostModalOpen} toggle={closePostModal} size="xl">
                <ModalHeader toggle={closePostModal}>
                  {activePreviewPost
                    ? `${formatDate(activePreviewPost.date)} · ${activePreviewPost.time}`
                    : "Post"}
                </ModalHeader>
                <ModalBody>
                  {activePreviewPost ? (
                    <div>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="font-weight-bold">
                          {activePreviewPost.title}
                        </div>
                        <div
                          className="social-auto__aiConfidence"
                          title={intl.formatMessage({
                            id: "app.socialAutomatedPosts.confidence.tooltip",
                            defaultMessage:
                              "Índice de aptitud de la pieza según la IA. Resume coherencia con tu marca, claridad del mensaje y potencial de engagement antes de publicar.",
                          })}
                        >
                          {intl.formatMessage(
                            {
                              id: "app.socialAutomatedPosts.confidence.label",
                              defaultMessage: "AI: {pct}%",
                            },
                            {
                              pct: Math.round(
                                activePreviewPost.confidence * 100
                              ),
                            }
                          )}
                        </div>
                      </div>

                      <div className="social-auto__postDetailGrid">
                        <aside className="social-auto__previewColumn">
                          <div className="social-auto__previewColumnTitle">
                            Vista previa
                          </div>
                          <input
                            ref={modalImageInputRef}
                            type="file"
                            accept="image/*"
                            className="d-none"
                            aria-hidden
                            onChange={handleChangePostImageByFile}
                          />
                      <div className="social-post-preview-container social-auto__posteadorPreview">
                          <div className="preview-switch-container">
                            <div className="preview-switch" role="tablist">
                              {SOCIAL_PREVIEWS.map((s) => {
                                const isSynced = !missingRequiredSocials.includes(
                                  s.id
                                );
                                return (
                                  <button
                                    key={s.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={s.id === activeSocialPreview}
                                    title={
                                      isSynced
                                        ? s.label
                                        : `${s.label} (vista previa)`
                                    }
                                    className={cn("preview-switch-button", {
                                      active: s.id === activeSocialPreview,
                                      "preview-switch-button--unsynced":
                                        !isSynced,
                                    })}
                                    onClick={() => setActiveSocialPreview(s.id)}
                                  >
                                    <div className="switch-icon">
                                      <SocialPreviewLogo
                                        socialId={s.id}
                                        active={s.id === activeSocialPreview}
                                      />
                                    </div>
                                    <span className="switch-label">{s.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="social-auto__posteadorPreviewShell">
                            <StaticSocialPostPreview
                              platform={activeSocialPreview}
                              text={activePreviewPost.text}
                              imageUrl={
                                isPostStillGenerating(activePreviewPost.id) &&
                                !postImageOverrides[activePreviewPost.id]
                                  ? null
                                  : activePreviewPost.image
                              }
                              displayName={companyName || currentProjectName || "Tu negocio"}
                              avatarUrl={logo?.previewUrl}
                              imageAspect={imageAspect}
                              compact
                              onImageClick={
                                activePreviewPost.image &&
                                !isPostStillGenerating(activePreviewPost.id)
                                  ? () => setImageEditorOpen(true)
                                  : undefined
                              }
                            />
                            {!activePreviewPost.image &&
                            jobState?.results[activePreviewPost.id]?.error ? (
                              <p className="social-auto__previewImageError">
                                {jobState.results[activePreviewPost.id]?.error}
                              </p>
                            ) : null}
                            {isEditingActiveImage ||
                            isPostStillGenerating(activePreviewPost.id) ||
                            isGeneratingPostText ? (
                              <div
                                className="social-auto__designEditOverlay"
                                title={
                                  isEditingActiveImage
                                    ? "Estamos realizando cambios en el diseño"
                                    : isGeneratingPostText &&
                                      !isPostStillGenerating(
                                        activePreviewPost.id
                                      )
                                    ? "Actualizando el texto con IA"
                                    : "Generando contenido"
                                }
                                role="status"
                                aria-live="polite"
                              >
                                <div
                                  className="social-auto__designEditOverlayShimmer"
                                  aria-hidden
                                />
                                <div className="social-auto__designEditOverlayCard">
                                  <Spinner color="light" size="sm" />
                                  <strong>
                                    {isEditingActiveImage
                                      ? intl.formatMessage({
                                          id: "app.socialAutomatedPosts.designEdit.overlayTitle",
                                          defaultMessage:
                                            "Estamos realizando cambios en el diseño",
                                        })
                                      : isGeneratingPostText &&
                                        !isPostStillGenerating(
                                          activePreviewPost.id
                                        )
                                      ? intl.formatMessage({
                                          id: "app.socialAutomatedPosts.modal.editText.applyingOverlay",
                                          defaultMessage:
                                            "Actualizando el texto con IA…",
                                        })
                                      : intl.formatMessage({
                                          id: "app.socialAutomatedPosts.generate.modalLoadingTitle",
                                          defaultMessage:
                                            "Generando el diseño con IA…",
                                        })}
                                  </strong>
                                  {isEditingActiveImage ? (
                                    <small>
                                      {intl.formatMessage(
                                        {
                                          id: "app.socialAutomatedPosts.designEdit.overlayProgress",
                                          defaultMessage: "Progreso: {progress}%",
                                        },
                                        { progress: nanoEditProgress }
                                      )}
                                    </small>
                                  ) : isPostStillGenerating(
                                      activePreviewPost.id
                                    ) ? (
                                    <small className="social-auto__designEditOverlayEta">
                                      {intl.formatMessage(
                                        {
                                          id: "app.socialAutomatedPosts.generate.modalLoadingEta",
                                          defaultMessage: "~{eta}s restantes",
                                        },
                                        {
                                          eta:
                                            etaSecForPost(
                                              activePreviewPost.id
                                            ) ??
                                            generationStats.etaSec ??
                                            1,
                                        }
                                      )}
                                    </small>
                                  ) : null}
                                  <span className="social-auto__designEditOverlayHint">
                                    {intl.formatMessage({
                                      id: "app.socialAutomatedPosts.designEdit.overlayHint",
                                      defaultMessage:
                                        "Podés cerrar esta ventana: el cambio continúa en segundo plano.",
                                    })}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                          <div
                            className="social-auto__previewAspectToggle"
                            role="group"
                            aria-label={intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.previewAspect.label",
                              defaultMessage: "Formato de la imagen",
                            })}
                          >
                            <span className="social-auto__previewAspectToggleLabel">
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.modal.previewAspect.label",
                                defaultMessage: "Formato",
                              })}
                            </span>
                            {IMAGE_ASPECT_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className={cn("social-auto__previewAspectBtn", {
                                  "social-auto__previewAspectBtn--active":
                                    imageAspect === opt.value,
                                })}
                                onClick={() => setImageAspect(opt.value)}
                              >
                                {opt.value === "1:1"
                                  ? intl.formatMessage({
                                      id: "app.socialAutomatedPosts.modal.previewAspect.square",
                                      defaultMessage: "Cuadrado",
                                    })
                                  : intl.formatMessage({
                                      id: "app.socialAutomatedPosts.modal.previewAspect.portrait",
                                      defaultMessage: "Vertical",
                                    })}
                              </button>
                            ))}
                          </div>
                          </div>
                          {showBackgroundWaitTip &&
                          (isEditingActiveImage || isGeneratingPostText) ? (
                            <div
                              className="social-auto__backgroundTip mt-2"
                              role="status"
                            >
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.background.waitTip",
                                defaultMessage:
                                  "Esto está demorando. Podés cerrar la ventana: seguimos trabajando en segundo plano.",
                              })}
                            </div>
                          ) : null}
                          {!hasAnySocialSynced ? (
                            <div className="social-auto__imageOnlyPreviewHint mt-2">
                              Vista previa disponible en todas las redes. Conectá
                              una cuenta para publicar.
                            </div>
                          ) : missingRequiredSocials.includes(
                              activeSocialPreview
                            ) ? (
                            <div className="social-auto__imageOnlyPreviewHint mt-2">
                              Vista previa de {SOCIAL_PREVIEWS.find((s) => s.id === activeSocialPreview)?.label}.
                              Esta red aún no está sincronizada.
                            </div>
                          ) : null}
                        </aside>

                        <section className="social-auto__editorColumn">

                      {activePreviewPost ? (
                        <div className="social-auto__modalSchedule mb-3">
                          <div className="social-auto__modalScheduleTitle">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.schedule.title",
                              defaultMessage: "Horario recomendado de la publicación (IA)",
                            })}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <DatePicker
                              className="social-auto__modalScheduleDate"
                              calendarClassName="social-auto__calendar"
                              format="dd/MM/y"
                              dayPlaceholder="dd"
                              monthPlaceholder="mm"
                              yearPlaceholder="aaaa"
                              value={activePreviewPost.date}
                              onChange={(value: Date | null) => {
                                if (!activePreviewPost || !value) return;
                                const time = activePreviewPost.time;
                                const next = { date: value, time };
                                setPostScheduleOverrides((prev) => ({
                                  ...prev,
                                  [activePreviewPost.id]: next,
                                }));
                                const caption = resolveCaptionAfterScheduleChange(
                                  activePreviewPost.id,
                                  value,
                                  time
                                );
                                setActivePreviewPost((prev) =>
                                  prev
                                    ? { ...prev, date: value, text: caption }
                                    : prev
                                );
                              }}
                            />
                            <input
                              type="time"
                              className="form-control social-auto__modalScheduleTime"
                              value={activePreviewPost.time}
                              onChange={(e) => {
                                const time = e.target.value;
                                if (!activePreviewPost) return;
                                const next = {
                                  date: activePreviewPost.date,
                                  time,
                                };
                                setPostScheduleOverrides((prev) => ({
                                  ...prev,
                                  [activePreviewPost.id]: next,
                                }));
                                const caption = resolveCaptionAfterScheduleChange(
                                  activePreviewPost.id,
                                  activePreviewPost.date,
                                  time
                                );
                                setActivePreviewPost((prev) =>
                                  prev ? { ...prev, time, text: caption } : prev
                                );
                              }}
                            />
                          </div>
                        </div>
                      ) : null}

                      {!hasOpenAiConfigured &&
                      isPlaceholderCaption(activePreviewPost.text) ? (
                        <div
                          className="alert alert-warning small py-2 mb-3"
                          role="status"
                        >
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.openai.notConfigured",
                            defaultMessage:
                              "La IA de Wembii no está disponible en este momento. Reintenta más tarde o contacta a soporte.",
                          })}
                        </div>
                      ) : null}

                      <div className="social-auto__postTextBlock">
                        <div className="social-auto__postTextBlockLabel">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.postText.label",
                            defaultMessage: "Texto de la publicación",
                          })}
                        </div>
                        <div className="social-auto__postTextBlockBody">
                          {activePreviewPost.text ||
                            "El texto aparecerá aquí cuando esté listo."}
                        </div>
                        <div className="social-auto__postTextBlockActions">
                          <Button
                            color="primary"
                            outline
                            size="sm"
                            onClick={requestAlternativePostText}
                            disabled={
                              isGeneratingPostText ||
                              !hasOpenAiConfigured ||
                              !activePreviewPost?.text ||
                              isPlaceholderCaption(activePreviewPost.text)
                            }
                          >
                            {isGeneratingPostText
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.applyingChanges",
                                  defaultMessage: "Aplicando cambios...",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.anotherOption",
                                  defaultMessage: "Otra opción de posteo",
                                })}
                          </Button>
                        </div>
                        <div className="social-auto__modalEditorHint mt-2 mb-0">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.freeEditsHint",
                            defaultMessage:
                              "Otra opción y solicitar cambios no consumen créditos (máx. 3 por post al mes).",
                          })}
                        </div>
                      </div>

                      <div className="social-auto__modalEditor mt-3">
                        <div className="social-auto__modalEditorTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.title",
                            defaultMessage: "Modificar texto",
                          })}
                        </div>
                        <div className="social-auto__modalEditorHint">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.changeHint",
                            defaultMessage:
                              "Pedile a la IA los cambios que querés (tono, largo, emojis, CTA, etc.). El nuevo texto se genera acá mismo.",
                          })}
                        </div>
                        <textarea
                          className="form-control social-auto__modalEditorTextarea social-auto__modalEditorTextarea--change"
                          value={textChangePrompt}
                          onChange={(e) => setTextChangePrompt(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.changePlaceholder",
                            defaultMessage:
                              "Ej: hacelo más corto, sumá un emoji al inicio y un CTA más urgente.",
                          })}
                          disabled={isGeneratingPostText}
                        />
                        <div className="social-auto__modalEditorActions">
                          <Button
                            color="primary"
                            outline
                            onClick={requestPostTextChanges}
                            disabled={
                              isGeneratingPostText ||
                              !activePreviewPost ||
                              !textChangePrompt.trim()
                            }
                          >
                            {isGeneratingPostText
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.applyingChanges",
                                  defaultMessage: "Aplicando cambios...",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editText.requestChanges",
                                  defaultMessage: "Solicitar cambios",
                                })}
                          </Button>
                        </div>
                        <div className="social-auto__modalEditorHint mt-3 mb-1">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.manualHint",
                            defaultMessage:
                              "También podés editar el texto a mano y guardarlo:",
                          })}
                        </div>
                        <textarea
                          className="form-control social-auto__modalEditorTextarea"
                          value={postTextDraft}
                          onChange={(e) => setPostTextDraft(e.target.value)}
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editText.placeholder",
                            defaultMessage:
                              "Escribe el texto de la publicación aquí...",
                          })}
                          disabled={isGeneratingPostText}
                        />
                        <div className="social-auto__modalEditorActions">
                          <Button
                            color="primary"
                            onClick={applyPostTextDraft}
                            disabled={
                              !postTextDraft.trim() || isGeneratingPostText
                            }
                          >
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.editText.applyBtn",
                              defaultMessage: "Guardar texto",
                            })}
                          </Button>
                        </div>
                      </div>

                      <div className="social-auto__modalEditor mt-3">
                        <div className="social-auto__modalEditorTitle">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editImage.title",
                            defaultMessage: "Editar imagen",
                          })}
                        </div>
                        <div className="social-auto__modalEditorHint">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editImage.hint",
                            defaultMessage:
                              "Describe el cambio y, si querés, adjuntá otra imagen o logo como referencia para que la IA lo use al editar.",
                          })}
                        </div>
                        <textarea
                          className="form-control social-auto__modalEditorTextarea"
                          placeholder={intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editNano.placeholder",
                            defaultMessage:
                              "Ej: usa más espacio en blanco, haz el logo más visible y cambia el fondo a azul claro.",
                          })}
                          value={nanoEditPrompt}
                          onChange={(e) => setNanoEditPrompt(e.target.value)}
                          disabled={isEditingActiveImage}
                        />
                        <div className="social-auto__editRefs">
                          <div className="social-auto__editRefsHeader">
                            <span>
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.modal.editImage.refsTitle",
                                defaultMessage:
                                  "Referencias (imagen o logo)",
                              })}
                            </span>
                            <Button
                              color="secondary"
                              outline
                              size="sm"
                              type="button"
                              onClick={() => editRefInputRef.current?.click()}
                              disabled={
                                isEditingActiveImage ||
                                editReferenceImages.length >= 3
                              }
                            >
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.modal.editImage.addRef",
                                defaultMessage: "Agregar referencia",
                              })}
                            </Button>
                            <input
                              ref={editRefInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              className="d-none"
                              aria-hidden
                              onChange={(e) => {
                                addEditReferenceImages(e.target.files);
                                e.target.value = "";
                              }}
                            />
                          </div>
                          <div className="social-auto__editRefsHint">
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.editImage.refsHint",
                              defaultMessage:
                                "Opcional. Hasta 3 archivos. La IA usará estas referencias junto con el diseño actual (y tu logo si está activo).",
                            })}
                          </div>
                          {editReferenceImages.length > 0 ? (
                            <div className="social-auto__editRefsGrid">
                              {editReferenceImages.map((img, idx) => (
                                <div
                                  key={`${img.previewUrl}-${idx}`}
                                  className="social-auto__editRefThumb"
                                >
                                  <img
                                    src={img.previewUrl}
                                    alt=""
                                  />
                                  <button
                                    type="button"
                                    className="social-auto__editRefRemove"
                                    onClick={() =>
                                      removeEditReferenceImage(idx)
                                    }
                                    disabled={isEditingActiveImage}
                                    aria-label={intl.formatMessage({
                                      id: "app.socialAutomatedPosts.modal.editImage.removeRef",
                                      defaultMessage: "Quitar referencia",
                                    })}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {includeLogo && logo?.previewUrl ? (
                            <div className="social-auto__editRefsLogoNote">
                              {intl.formatMessage({
                                id: "app.socialAutomatedPosts.modal.editImage.logoAlsoUsed",
                                defaultMessage:
                                  "También se enviará el logo de marca de la configuración.",
                              })}
                            </div>
                          ) : null}
                        </div>
                        <div className="social-auto__modalEditorActions">
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            onClick={() => modalImageInputRef.current?.click()}
                            disabled={isEditingActiveImage}
                          >
                            {intl.formatMessage({
                              id: "app.socialAutomatedPosts.modal.changeImage.btn",
                              defaultMessage: "Reemplazar imagen",
                            })}
                          </Button>
                          <div
                            className="social-auto__modalEditorProgress text-muted small"
                            aria-live="polite"
                          >
                            {isEditingActiveImage
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.loading",
                                  defaultMessage: `Actualizando diseño... ${nanoEditProgress}%`,
                                })
                              : null}
                          </div>
                          <Button
                            color="primary"
                            onClick={() => {
                              void handleEditActivePostImage();
                            }}
                            disabled={
                              isEditingActiveImage ||
                              !nanoEditPrompt.trim() ||
                              !activePreviewPost
                            }
                          >
                            {isEditingActiveImage
                              ? intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.btnLoading",
                                  defaultMessage: "Aplicando cambios...",
                                })
                              : intl.formatMessage({
                                  id: "app.socialAutomatedPosts.modal.editNano.btn",
                                  defaultMessage: "Aplicar cambios con IA",
                                })}
                          </Button>
                        </div>
                        <p className="social-auto__modalEditorCreditHint">
                          {intl.formatMessage({
                            id: "app.socialAutomatedPosts.modal.editImage.creditHint",
                            defaultMessage:
                              "Aplicar cambios con IA consume 1 crédito. Reemplazar la imagen manualmente no consume créditos.",
                          })}
                        </p>
                      </div>
                        </section>
                      </div>
                    </div>
                  ) : null}
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={closePostModal}>
                    Cerrar
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={isGenerationConfirmOpen}
                toggle={() => setIsGenerationConfirmOpen(false)}
                centered
              >
                <ModalHeader toggle={() => setIsGenerationConfirmOpen(false)}>
                  Crear contenido automáticamente
                </ModalHeader>
                <ModalBody>
                  <div className="social-auto__generationConfirm">
                    <div className="social-auto__generationConfirmIcon" aria-hidden>
                      ✦
                    </div>
                    <div>
                      <strong>
                        Se crearán {previewDates.length} publicaciones para
                        revisar.
                      </strong>
                      <p className="mb-0">
                        La generación continuará aunque navegues por la app.
                        Cada pieza y su texto quedarán disponibles en el
                        calendario a medida que estén listos. Al terminar,
                        recibirás una notificación en la campanita.
                      </p>
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="secondary"
                    outline
                    onClick={() => setIsGenerationConfirmOpen(false)}
                  >
                    Seguir editando
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => {
                      setIsGenerationConfirmOpen(false);
                      startGeneration(true);
                    }}
                  >
                    Crear automáticamente
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={syncAccountsModalOpen}
                toggle={() => setSyncAccountsModalOpen(false)}
                size="lg"
              >
                <ModalHeader toggle={() => setSyncAccountsModalOpen(false)}>
                  {intl.formatMessage({
                    id: "app.socialAutomatedPosts.syncModalTitle",
                    defaultMessage: "Sincronizar redes sociales",
                  })}
                </ModalHeader>
                <ModalBody>
                  <div className="mb-3">
                    {!hasAnySocialSynced ? (
                      intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalNoneSynced",
                        defaultMessage:
                          "Ninguna red está sincronizada. Conecta al menos una a continuación para poder programar publicaciones.",
                      })
                    ) : (
                      intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalHint",
                        defaultMessage:
                          "Conecta las redes que faltan para poder programar las publicaciones:",
                      })
                    )}
                  </div>

                  <div>
                    {getAutoPostRequiredSocials().filter((s) =>
                      missingRequiredSocials.includes(s.previewId)
                    ).map((s) => (
                      <div key={s.previewId} className="mb-3">
                        <SyncAccount
                          network={s.providerId}
                          existingAccounts={[] as any}
                          onSync={refreshSocialSettings}
                          onDisconnect={refreshSocialSettings}
                        />
                      </div>
                    ))}
                  </div>
                  {missingRequiredSocials.length === 0 && (
                    <div className="text-success font-weight-bold mt-2">
                      {intl.formatMessage({
                        id: "app.socialAutomatedPosts.syncModalAllDone",
                        defaultMessage: "Todas las redes están sincronizadas. Puedes cerrar y enviar al calendario.",
                      })}
                    </div>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="secondary"
                    onClick={() => setSyncAccountsModalOpen(false)}
                  >
                    {intl.formatMessage({
                      id: "app.close",
                      defaultMessage: "Cerrar",
                    })}
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={scheduleNetworksModalOpen}
                toggle={() =>
                  !isGenerating && setScheduleNetworksModalOpen(false)
                }
                centered
              >
                <ModalHeader
                  toggle={() =>
                    !isGenerating && setScheduleNetworksModalOpen(false)
                  }
                >
                  {intl.formatMessage({
                    id: "app.socialAutomatedPosts.schedule.networksTitle",
                    defaultMessage: "¿En qué redes quieres programar?",
                  })}
                </ModalHeader>
                <ModalBody>
                  <p className="text-muted small mb-3">
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.schedule.networksHint",
                      defaultMessage:
                        "Elige las redes sincronizadas donde se publicarán las piezas seleccionadas.",
                    })}
                  </p>
                  <div className="social-auto__scheduleNetworks">
                    {getAutoPostRequiredSocials()
                      .filter((s) =>
                        scheduleEligibleNetworks.includes(s.previewId)
                      )
                      .map((s) => {
                        const label =
                          SOCIAL_PREVIEWS.find((p) => p.id === s.previewId)
                            ?.label || s.previewId;
                        const checked = Boolean(
                          selectedScheduleNetworks[s.previewId]
                        );
                        return (
                          <label
                            key={s.previewId}
                            className={cn(
                              "social-auto__scheduleNetworkRow",
                              {
                                "social-auto__scheduleNetworkRow--selected":
                                  checked,
                              }
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isGenerating}
                              onChange={(e) => {
                                const next = e.target.checked;
                                setSelectedScheduleNetworks((prev) => ({
                                  ...prev,
                                  [s.previewId]: next,
                                }));
                              }}
                            />
                            <SocialPreviewLogo
                              socialId={s.previewId}
                              active={checked}
                            />
                            <span className="social-auto__scheduleNetworkLabel">
                              {label}
                            </span>
                            {checked ? (
                              <span className="social-auto__scheduleNetworkBadge">
                                {intl.formatMessage({
                                  id: "app.socialAutomatedPosts.schedule.networkSelected",
                                  defaultMessage: "Seleccionada",
                                })}
                              </span>
                            ) : null}
                          </label>
                        );
                      })}
                    {!scheduleEligibleNetworks.length ? (
                      <div className="social-auto__scheduleNetworksEmpty">
                        {intl.formatMessage({
                          id: "app.socialAutomatedPosts.schedule.networksEmpty",
                          defaultMessage:
                            "Todavía no hay redes sincronizadas. Conéctalas para poder programar.",
                        })}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="social-auto__scheduleSyncMore"
                      disabled={isGenerating}
                      onClick={() => setSyncAccountsModalOpen(true)}
                    >
                      <span className="social-auto__scheduleSyncMoreIcon" aria-hidden>
                        +
                      </span>
                      <span>
                        {scheduleEligibleNetworks.length
                          ? intl.formatMessage({
                              id: "app.socialAutomatedPosts.schedule.syncMore",
                              defaultMessage: "Sincronizar más redes sociales",
                            })
                          : intl.formatMessage({
                              id: "app.socialAutomatedPosts.syncNow",
                              defaultMessage: "Sincronizar ahora",
                            })}
                      </span>
                    </button>
                  </div>
                </ModalBody>
                <ModalFooter className="social-auto__scheduleNetworksFooter">
                  <Button
                    color="secondary"
                    outline
                    disabled={isGenerating}
                    onClick={() => setScheduleNetworksModalOpen(false)}
                  >
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.schedule.cancel",
                      defaultMessage: "Cancelar",
                    })}
                  </Button>
                  <Button
                    color="primary"
                    className="social-auto__scheduleConfirmBtn"
                    disabled={
                      isGenerating ||
                      !Object.values(selectedScheduleNetworks).some(Boolean)
                    }
                    onClick={() => {
                      void handleCreate();
                    }}
                  >
                    {isGenerating
                      ? intl.formatMessage({
                          id: "app.socialAutomatedPosts.creating",
                          defaultMessage: "Programando...",
                        })
                      : intl.formatMessage({
                          id: "app.socialAutomatedPosts.schedule.confirmNetworks",
                          defaultMessage: "Programar ahora",
                        })}
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal
                isOpen={scheduleSuccessModalOpen}
                toggle={() => setScheduleSuccessModalOpen(false)}
                centered
              >
                <ModalHeader toggle={() => setScheduleSuccessModalOpen(false)}>
                  {intl.formatMessage({
                    id: "app.socialAutomatedPosts.schedule.successTitle",
                    defaultMessage: "Publicaciones programadas",
                  })}
                </ModalHeader>
                <ModalBody>
                  <p className="mb-2">
                    {intl.formatMessage(
                      {
                        id: "app.socialAutomatedPosts.schedule.successBody",
                        defaultMessage:
                          "Se programaron {count} envíos correctamente{failed}.",
                      },
                      {
                        count: scheduleSuccessStats?.sent ?? 0,
                        failed:
                          (scheduleSuccessStats?.failed ?? 0) > 0
                            ? intl.formatMessage(
                                {
                                  id: "app.socialAutomatedPosts.schedule.successFailedSuffix",
                                  defaultMessage:
                                    " ({fail} con error)",
                                },
                                { fail: scheduleSuccessStats?.failed ?? 0 }
                              )
                            : "",
                      }
                    )}
                  </p>
                  <p className="text-muted small mb-0">
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.schedule.successNext",
                      defaultMessage:
                        "Puedes revisarlas en el calendario de redes o seguir agendando más piezas aquí.",
                    })}
                  </p>
                </ModalBody>
                <ModalFooter className="social-auto__scheduleSuccessActions">
                  <Button
                    color="primary"
                    onClick={() => {
                      setScheduleSuccessModalOpen(false);
                      goToSocialCalendar();
                    }}
                  >
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.schedule.goToCalendar",
                      defaultMessage: "Ver en el calendario",
                    })}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={() => {
                      setScheduleSuccessModalOpen(false);
                      setCurrentProjectId(null);
                      setStep("config");
                      goToSocialAutomatedPosts();
                    }}
                  >
                    {intl.formatMessage({
                      id: "app.socialAutomatedPosts.schedule.scheduleMore",
                      defaultMessage: "Agendar otras publicaciones",
                    })}
                  </Button>
                </ModalFooter>
              </Modal>

              <AiImageViewerEditorModal
                isOpen={imageEditorOpen}
                imageSrc={activePreviewPost?.image || null}
                onClose={() => setImageEditorOpen(false)}
                onApply={handleApplyEditedImageBlob}
                busy={isEditingActiveImage}
                enableAiEdit
                aiEditBusy={isEditingActiveImage}
                companyId={companyId}
                onAiEdit={async (prompt) => {
                  await handleEditActivePostImage(prompt);
                }}
                aiCreditHint={intl.formatMessage({
                  id: "app.socialAutomatedPosts.modal.editImage.creditHint",
                  defaultMessage:
                    "Aplicar cambios con IA consume 1 crédito. Reemplazar la imagen manualmente no consume créditos.",
                })}
              />
            </div>
          </div>
        </div>
      </Content>
    </Layout>
  );
}

